import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Badge, CardHeader } from "reactstrap";
import "./index.css";
import { Order, OrderItem } from "../OrderList";
import BreadCrumbs from "../../../components/BreadCrumbs";
import {
  Button,
  Col,
  Placeholder,
  Row,
  Card,
  CardBody,
  Modal,
  Form,
  Table,
  Accordion,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuildingUn,
  faCalendarAlt,
  faChevronDown,
  faChevronUp,
  faCoins,
  faCopy,
  faCreditCard,
  faMoneyCheckAlt,
  faPrint,
  faRightLeft,
  faShare,
  faSpinner,
  faStore,
  faUserTie,
  faMapMarkerAlt,
  faPhone,
  faUser,
  faTruck,
  faBox,
  faEye,
  faReceipt,
  faMotorcycle,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import StatusUpdateModal from "../OverViewModalNew";
import {
  capitalizeFirstLetter,
  formatFirestoreTimestamp,
} from "../../../utility/Utility";
import {
  COMPLETED_ORDER_STATUS,
  ISSUED_BY_KITCHEN_ORDER_STATUS,
  PACKING_COMPLETED_ORDER_STATUS,
  READY_AT_KITCHEN_ORDER_STATUS,
} from "../../../constants/constans";
import OutlineButtonComponent from "../../../components/OutlineButtonComponent";
import { useIssueOrderMutation } from "../../../redux/services/directIssue";
import { toast } from "react-toastify";
import {
  useUpdateOrderMutation,
  useGetOrderPaymentBreakdownMutation,
  useIssuePickupOrderMutation,
  useAssignDeliveryPartnerToOrderMutation,
  useDispatchDeliveryOrderForDeliveryPartnerMutation,
} from "../../../redux/services/orderService";
import {
  useFirebaseCartSummaries,
  useFirebaseOrderById,
  useFirebaseChildOrders,
} from "../../../hooks/useFirebaseOrders";
import { useFirebaseCartById, useFirebaseCartOwnerById } from "../../../hooks/useCartDetail";
import TableLoadingComponent from "../../../components/TableLoadingComponent";
import TentativeItemsEditModal from "./TentativeItemsEditModal";
import CollectItemsStep, {
  Menu as CO_Menu,
} from "../CreateOrder/CollectItemsStep";
import { useForm } from "react-hook-form";
import { useLazyGetMenuListQuery } from "../../../redux/services/MenusService";
import { useLazyGetMenuItemsByRestaurantIdQuery } from "../../../redux/services/menuItemService";
import { useLazyGetMenuListByRestaurantIdQuery } from "../../../redux/services/setMenuService";
import { useLazyFetchCustomMenuListQuery } from "../../../redux/services/customizeMenuService";
import { MenuItem as ReduxMenuItem } from "../../../redux/menuSlice";
import { SetMenu } from "../../../redux/setMenuSlice";
import { CustomMenuItem } from "../../../redux/customizeMenuSlice";
import {
  getCurrentRole,
  hasPermission,
} from "../../../redux/services/permissionService";
import ButtonComponent from "../../../components/ButtonComponent";
import useOrderPrint from "../../../hooks/useOrderPrint";
import { useSelector } from "react-redux";
import Select from "react-select";
import DriverLocationModal from "../../../components/DriverLocationModal";

const OrderView: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { state } = location;
  const orderFromState: Order | undefined = state?.order;
  const orderId = orderFromState?.id || state?.orderId;

  // Use Firebase real-time hook for order data
  const { data: orderData, loading: isLoadingOrderData } = useFirebaseOrderById(
    orderId || "",
  );
  // Use the new hook to fetch child orders if this is a parent order
  const { data: childOrdersData, loading: isLoadingChildOrders } =
    useFirebaseChildOrders(
      orderData?.isSplitOrder === true ? orderId || "" : "",
    );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>(
    {},
  );
  const [isOrderIssued, setIsOrderIssued] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [issuedMethod, setIssuedMethod] = useState("");
  const [isCardIssued, setIsCardIssued] = useState<boolean>(false);
  const [isCashIssued, setIsCashIssued] = useState<boolean>(false);
  const [isEditItemsOpen, setIsEditItemsOpen] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [editedItems, setEditedItems] = useState<OrderItem[] | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [expandedChildOrders, setExpandedChildOrders] = useState<{
    [key: string]: boolean;
  }>({});
  const isChildOrder = useMemo(() => {
    return orderData?.isSplitOrder === true && orderData?.parentOrderId;
  }, [orderData]);

  // New state for pickup order payment modal
  const [showPickupPaymentModal, setShowPickupPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<string>("Cash");

  // State for driver location modal
  const [showDriverLocationModal, setShowDriverLocationModal] = useState(false);
  const [selectedChildOrderForTracking, setSelectedChildOrderForTracking] = useState<any | null>(null);

  // State for delivery partner selection
  const [selectedDeliveryPartner, setSelectedDeliveryPartner] = useState<{ value: string; label: string } | null>(null);
  const [deliveryPartnerOptions, setDeliveryPartnerOptions] = useState<{ value: string; label: string }[]>([]);
  const [isChangingDeliveryPartner, setIsChangingDeliveryPartner] = useState(false);

  // Fetch main cart data to get deliveryCartsIds
  const { data: mainCartData } = useFirebaseCartById(orderData?.cart?.cartId || "");
  const { data: deliveryCartData } = useFirebaseCartById(orderData?.deliveryCart?.cartId || "");
  const { data: deliveryPersonData } = useFirebaseCartOwnerById(
    deliveryCartData?.owner?.id || orderData?.deliveryCart?.owner?.id || "",
  );

  // Initialize the print hook
  const { printOrder, hidePreview, previewVisible, previewHTML, isPrinting } =
    useOrderPrint({
      orders: orderData ? [orderData as any] : [],
    });

  // Local form/control for reusing CollectItemsStep
  const { control: editControl, watch: editWatch } = useForm({
    defaultValues: {
      orderBy: { value: "CartPerson" },
      isScheduledOrder: orderData?.isScheduledOrder || false,
      date: orderData?.deliverAt ? (typeof orderData.deliverAt === 'object' && orderData.deliverAt.seconds ? new Date(orderData.deliverAt.seconds * 1000) : new Date(orderData.deliverAt)) : new Date(),
      organizationId: orderData?.organization?.organizationId || orderData?.organization?.id || null,
    },
  });

  // Reuse CreateOrder data flows for menus and items
  const [menus, setMenus] = useState<CO_Menu[]>([]);
  const [singleMenuItemsList, setSingleMenuItemsList] = useState<
    ReduxMenuItem[]
  >([]);
  const [comboItemList, setComboItemList] = useState<SetMenu[]>([]);
  const [customMenuItemList, setCustomMenuItemList] = useState<
    CustomMenuItem[]
  >([]);
  const [selectedMenu, setSelectedMenu] = useState<CO_Menu | undefined>(
    undefined,
  );
  const [selectedMenuItemList, setSelectedMenuItemList] = useState<
    ReduxMenuItem[]
  >([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filteredData, setFilteredData] = useState<ReduxMenuItem[]>([] as any);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(false);

  const selectedRestaurantData = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails,
  );
  const [triggerMenuList, { data: menuListData, isLoading: isMenuLoading }] =
    useLazyGetMenuListQuery();
  const [
    triggerMenuItems,
    { data: menuItemsData, isLoading: isMenuItemLoading },
  ] = useLazyGetMenuItemsByRestaurantIdQuery();
  const [triggerSetMenus, { data: setMenuData, isLoading: isSetMenuLoading }] =
    useLazyGetMenuListByRestaurantIdQuery();
  const [
    triggerCustomMenus,
    { data: customMenuData, isLoading: isCustomMenuLoading },
  ] = useLazyFetchCustomMenuListQuery();

  const isTentative = [
    "tentative",
    "pending",
    "approvedbyrestaurant",
    "preparing",
    "readyatkitchen",
  ].includes((orderData?.orderStatus || "").toLowerCase());

  const canIssueOrders = hasPermission("feat_order_issue", "edit");
  const userRole = getCurrentRole();

  // Add this function to check if cart has delivery person assigned
  const hasCartDeliveryPerson = useMemo(() => {
    return (
      orderData?.cart?.cartPersonId !== undefined &&
      orderData?.cart?.cartPersonId !== null &&
      orderData?.cart?.cartPersonId !== ""
    );
  }, [orderData?.cart?.cartPersonId]);

  // Check if this is a parent order
  const isParentOrder = useMemo(() => {
    // Parent order has isSplitOrder: true AND no parentOrderId
    return orderData?.isSplitOrder === true && !orderData?.parentOrderId;
  }, [orderData]);

  // const isNormalOrder = useMemo(() => {
  //   return (orderData?.isDeliveryOrder) && (orderData?.isSplitOrder !== true);
  // }, [orderData]);

  // Get child orders
  const childOrders = useMemo(() => {
    return childOrdersData?.orders || [];
  }, [childOrdersData]);

  // Check if parent order can be cancelled (no child order has reached PackingComplete or beyond)
  const canCancelParentOrder = useMemo(() => {
    if (!isParentOrder || childOrders.length === 0) return true;

    const restrictedStatuses = ["PackingComplete", "Dispatched", "OutForDelivery", "Delivered"];
    return !childOrders.some((childOrder: any) =>
      restrictedStatuses.includes(childOrder.deliveryStatus)
    );
  }, [isParentOrder, childOrders]);

  // Check if edit features should be shown (tentative AND not parent order)
  const shouldShowEditFeatures = useMemo(() => {
    return isTentative && !isParentOrder;
  }, [isTentative, isParentOrder]);

  const handleAddNewOrder = () => {
    navigate("/orders/create-order");
  };

  const handleReorder = () => {
    if (!orderData) return;

    navigate("/orders/create-order", {
      state: {
        reorderData: {
          isDeliveryOrder: orderData.isDeliveryOrder ?? false,
          orderCreatedBy: orderData.orderCreatedBy,
          customer: orderData.customer ?? null,
          cart: orderData.cart ?? null,
          organization: orderData.organization ? {
            organizationId: orderData.organization.organizationId || orderData.organization.id,
            id: orderData.organization.id || orderData.organization.organizationId,
            displayName: orderData.organization.displayName,
            phoneNumber: orderData.organization.phoneNumber,
          } : null,
          phoneNumber: orderData.phoneNumber ?? orderData.customer?.phoneNumber ?? null,
          paymentMethod: orderData.paymentMethod ?? null,
          orderNote: orderData.orderNote ?? "",
          packingNote: orderData.packingNote ?? "",
          paymentNote: orderData.paymentNote ?? "",
          deliveryDetails: orderData.deliveryDetails ?? null,
          deliveryTimeSlot: orderData.deliveryTimeSlot ?? null,
          pickupTimeSlot: orderData.pickupTimeSlot ?? null,
          isManualDeliveryFee: orderData.paymentSummary?.isManualDeliveryFee ?? false,
          manualDeliveryFee: orderData.paymentSummary?.isManualDeliveryFee
            ? (orderData.paymentSummary?.deliveryFee ?? 0)
            : null,
          paymentSummary: orderData.paymentSummary ?? null,
          orderItems: (orderData.orderItems || []).map((item: any) => ({
            id: item.menuItemId,
            menuItemId: item.menuItemId,
            tinyId: item.tinyId,
            skuId: item.skuId,
            displayName: item.displayName,
            price: item.unitPrice,
            calculatedPrice: item.unitPrice,
            quantity: item.quantity,
            portionLabel: item.portionSize || "Regular",
            itemType: item.itemType,
            customItems: item.customItems || [],
          })),
        },
      },
    });
  };

  const handleViewActivity = () => {
    const targetOrderId = orderData?.id || orderId;
    if (!targetOrderId) {
      toast.error("Order ID is missing");
      return;
    }

    navigate("/orders/activity/view", {
      state: {
        orderId: targetOrderId,
        fromPath: location.pathname,
      },
    });
  };

  // Handler for assigning delivery partner
  const handleAssignDeliveryPartner = async () => {
    if (!selectedDeliveryPartner || !orderId) {
      toast.error("Please select a delivery partner");
      return;
    }

    try {
      const response = await assignDeliveryPartnerToOrder({
        orderId,
        deliveryCartId: selectedDeliveryPartner.value,
      }).unwrap();

      toast.success(
        response?.message ||
        `Delivery partner assigned: ${selectedDeliveryPartner.label}`,
      );
      setIsChangingDeliveryPartner(false);
    } catch (error) {
      const errorMessage =
        (error as any)?.data?.message || "Failed to assign delivery partner";
      toast.error(errorMessage);
      console.error(error);
    }
  };

  const handleChangeDeliveryPartnerClick = () => {
    if (orderData?.deliveryCart?.cartId && orderData?.deliveryCart?.displayName) {
      setSelectedDeliveryPartner({
        value: orderData.deliveryCart.cartId,
        label: orderData.deliveryCart.displayName,
      });
    }
    setIsChangingDeliveryPartner(true);
  };

  const handleDispatchOrder = () => {
    if (!orderData?.id && !orderId) {
      toast.error("Order ID is missing");
      return;
    }

    dispatchDeliveryOrderForDeliveryPartner({
      orderId: orderData?.id || orderId,
    })
      .unwrap()
      .then((response: any) => {
        toast.success(response?.message || "Order dispatched successfully");
      })
      .catch((error: any) => {
        toast.error(error?.data?.message || "Failed to dispatch order");
      });
  };

  // Print functions
  // Handler functions for driver location modal
  const handleDriverLocationClick = (childOrder?: any) => {
    if (childOrder) {
      setSelectedChildOrderForTracking(childOrder);
    } else {
      setSelectedChildOrderForTracking(null);
    }
    setShowDriverLocationModal(true);
  };

  const handleCloseDriverLocationModal = () => {
    setShowDriverLocationModal(false);
    setSelectedChildOrderForTracking(null);
  };

  const handlePrintRiderCopy = async () => {
    if (!orderData?.id || !orderData?.orderReference) {
      toast.error("Order data is not available for printing");
      return;
    }

    try {
      await printOrder({
        orderId: orderData.id,
        orderReference: orderData.orderReference,
        copyType: "rider",
      });
    } catch (error) {
      toast.error("Failed to print rider copy");
    }
  };

  const handlePrintCustomerCopy = async () => {
    if (!orderData?.id || !orderData?.orderReference) {
      toast.error("Order data is not available for printing");
      return;
    }

    try {
      await printOrder({
        orderId: orderData.id,
        orderReference: orderData.orderReference,
        copyType: "customer",
      });
    } catch (error) {
      toast.error("Failed to print customer copy");
    }
  };

  useEffect(() => {
    // Only load menus and items if it's a tentative order AND not a parent order
    if (!orderData?.restaurantId || !shouldShowEditFeatures) return;
    setIsInitialLoading(true);
    triggerMenuList(orderData.restaurantId);
    triggerMenuItems(orderData.restaurantId);
    triggerSetMenus(orderData.restaurantId);
    triggerCustomMenus(orderData.restaurantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData?.restaurantId, shouldShowEditFeatures]);

  useEffect(() => {
    const loading =
      isMenuLoading ||
      isMenuItemLoading ||
      isSetMenuLoading ||
      isCustomMenuLoading;
    setIsInitialLoading(loading);
  }, [
    isMenuLoading,
    isMenuItemLoading,
    isSetMenuLoading,
    isCustomMenuLoading,
    shouldShowEditFeatures,
  ]);

  // Fetch delivery carts when mainCartData changes
  useEffect(() => {
    const fetchDeliveryCarts = async () => {
      if (!mainCartData) {
        setDeliveryPartnerOptions([]);
        return;
      }

      const deliveryCartIds = (mainCartData.deliveryCartsIds || mainCartData.deliveryCartsIds || []) as string[];

      if (deliveryCartIds.length === 0) {
        setDeliveryPartnerOptions([]);
        return;
      }

      // Fetch all delivery carts using Firebase
      const { db } = await import("../../../firebase");
      const { doc, getDoc } = await import("firebase/firestore");

      const cartPromises = deliveryCartIds.map(async (cartId: string) => {
        try {
          const cartDocRef = doc(db, "Carts", cartId);
          const cartDoc = await getDoc(cartDocRef);
          if (cartDoc.exists()) {
            return {
              id: cartDoc.id,
              ...cartDoc.data()
            };
          }
          return null;
        } catch (error) {
          console.error(`Error fetching cart ${cartId}:`, error);
          return null;
        }
      });

      const carts = await Promise.all(cartPromises);
      const validCarts = carts.filter(Boolean);

      const options = validCarts.map((cart: any) => ({
        value: cart.id,
        label: cart.displayName || "Unknown Cart",
      }));

      setDeliveryPartnerOptions(options);
    };

    fetchDeliveryCarts();
  }, [mainCartData]);

  // Copy order details to clipboard function
  const copyOrderDetails = async () => {
    if (!orderData) return;

    // Get customer/cart/organization name
    let customerName = "";
    if (
      orderData.orderCreatedBy?.toLowerCase() === "customer" &&
      orderData.customer?.displayName
    ) {
      customerName = orderData.customer.displayName;
    } else if (
      orderData.orderCreatedBy?.toLowerCase() === "cartperson" &&
      orderData.cart?.displayName
    ) {
      customerName = orderData.cart.displayName;
    } else if (orderData.orderCreatedBy?.toLowerCase() === "restaurant") {
      if (orderData.organization?.displayName) {
        customerName = orderData.organization.displayName;
      } else if (orderData.customer?.displayName) {
        customerName = orderData.customer.displayName;
      } else if (orderData.cart?.displayName) {
        customerName = orderData.cart.displayName;
      }
    } else {
      customerName = orderData.customer?.displayName || "N/A";
    }

    // Get order items
    const displayedItems: any[] = shouldShowEditFeatures
      ? (editableOrderItems || []).map((it: any) => ({
        displayName: it.displayName,
        unitPrice: it.calculatedPrice ?? it.price ?? 0,
        quantity: it.quantity ?? 0,
        itemType: it.itemType,
        portionSize: it.portionLabel,
        customItems: it.customItems,
      }))
      : orderData?.orderItems || [];

    const sortedItems = [...displayedItems].sort((a: any, b: any) => {
      const orderPriority: { [key in ItemType]: number } = {
        MenuItem: 1,
        SetMenu: 2,
        CustomMenu: 3,
      };
      return (
        orderPriority[a.itemType as ItemType] -
        orderPriority[b.itemType as ItemType]
      );
    });

    // Format order details text
    let orderText = ``;
    orderText += `*Order Number:* ${orderData.orderReference || "N/A"}\n`;

    // Determine if this is an organization order without customer
    const isOrganizationOrder =
      orderData.orderCreatedBy?.toLowerCase() === "restaurant" &&
      orderData.organization?.displayName &&
      !orderData.customer?.displayName;

    const customerLabel = isOrganizationOrder ? "Organization" : "Customer";
    orderText += `*${customerLabel}:* ${customerName}\n`;

    // Add phone number if available
    const phoneNumber =
      orderData.phoneNumber || orderData.customer?.phoneNumber;
    if (phoneNumber) {
      orderText += `*Phone:* ${phoneNumber}\n`;
    }

    // Add delivery information
    if (orderData.isDeliveryOrder) {
      orderText += `\n*DELIVERY INFO*\n`;
      if (orderData.deliveryTimeSlot) {
        orderText += `*Time:* ${orderData.deliveryTimeSlot}\n`;
      } else if (orderData.deliverAt) {
        orderText += `*Time:* ${formatFirestoreTimestamp(
          orderData.deliverAt,
        )}\n`;
      }
      if (orderData.deliveryDetails?.address) {
        orderText += `*Address:* ${orderData.deliveryDetails.address}\n`;
      }
      if (orderData.deliveryDetails?.deliveryContactName) {
        orderText += `*Contact:* ${orderData.deliveryDetails.deliveryContactName}\n`;
      }
      if (orderData.deliveryDetails?.deliveryContactPhoneNumber) {
        orderText += `*Phone:* ${orderData.deliveryDetails.deliveryContactPhoneNumber}\n`;
      }
      if (orderData.deliveryDetails?.deliveryInstructions) {
        orderText += `*Instructions:* ${orderData.deliveryDetails.deliveryInstructions}\n`;
      }
    } else {
      // Add pickup cart information for non-delivery orders
      orderText += `\n*PICKUP INFO*\n`;
      if (orderData.cart?.displayName) {
        orderText += `*Cart:* ${orderData.cart.displayName}\n`;
      }
    }
    if (orderData.orderNote) {
      orderText += `\n*Order Note:* ${orderData.orderNote}\n`;
    }
    if (orderData.packingNote) {
      orderText += `*Packing Note:* ${orderData.packingNote}\n`;
    }
    if (orderData.paymentNote) {
      orderText += `*Payment Note:* ${orderData.paymentNote}\n`;
    }

    // Add order items
    orderText += `\n*ORDER ITEMS*\n`;
    sortedItems.forEach((item: any, index: number) => {
      orderText += `${index + 1}. ${item.displayName} ${item.itemType === "CustomMenu" ? "(Custom Item)\n" : "\n"
        }`;
      orderText += `   • Qty: ${item.quantity} | Price: LKR ${(
        item.unitPrice || 0
      ).toFixed(2)}\n`;
      orderText += `   • Total: LKR ${(
        (item.unitPrice || 0) * item.quantity
      ).toFixed(2)}\n`;

      // Add custom items if this is a CustomMenu item
      if (
        item.itemType === "CustomMenu" &&
        item.customItems &&
        item.customItems.length > 0
      ) {
        item.customItems.forEach((customItem: any) => {
          orderText += `      ◦ ${customItem.displayName}\n`;
          orderText += `        Qty: ${customItem.quantity} | Price: LKR ${(
            customItem.unitPrice || 0
          ).toFixed(2)}\n`;
        });
      }

      orderText += `\n`;
    });

    // Add payment details
    orderText += `*PAYMENT DETAILS*\n`;

    // Get payment method - check for promo code first
    let paymentMethod = orderData.paymentMethod || "N/A";
    if (orderData.promoCode) {
      paymentMethod = "Promo";
    } else if (
      !orderData.isDeliveryOrder &&
      orderData.paymentMethod === "CardOnDelivery"
    ) {
      paymentMethod = "Card On Pick up";
    }

    orderText += `*Method:* ${paymentMethod === "CashPlusWallet"
      ? "Cash & Wallet"
      : paymentMethod === "CardPlusWallet"
        ? "Card & Wallet"
        : paymentMethod
      }\n`;

    if (orderData.paymentSummary) {
      orderText += `*Sub Total:* LKR ${(
        orderData.paymentSummary.totalPrice ||
        orderData.totalPrice ||
        0
      ).toFixed(2)}\n`;

      if (orderData.isDeliveryOrder && orderData.paymentSummary.deliveryFee) {
        if (orderData.paymentSummary.isDeliveryFree) {
          orderText += `*Delivery Fee:* FREE (LKR ${orderData.paymentSummary.deliveryFee.toFixed(
            2,
          )})\n`;
        } else {
          orderText += `*Delivery Fee:* LKR ${orderData.paymentSummary.deliveryFee.toFixed(
            2,
          )}\n`;
        }
      }

      if (orderData.paymentSummary.walletPaymentAmount) {
        orderText += `*Wallet Payment:* LKR ${orderData.paymentSummary.walletPaymentAmount.toFixed(
          2,
        )}\n`;
      }
      if (orderData.paymentSummary.cardPaymentAmount) {
        orderText += `*Card Payment:* LKR ${orderData.paymentSummary.cardPaymentAmount.toFixed(
          2,
        )}\n`;
      }
      if (orderData.paymentSummary.cashPaymentAmount) {
        orderText += `*Cash Payment:* LKR ${orderData.paymentSummary.cashPaymentAmount.toFixed(
          2,
        )}\n`;
      }
      // Show discount if available
      const discountVal = orderData.paymentSummary.discount ?? null;
      if (discountVal !== null && discountVal !== undefined) {
        try {
          orderText += `*Discount:* LKR ${Number(discountVal).toFixed(2)}\n`;
        } catch (e) {
          // ignore formatting errors
          orderText += `*Discount:* ${discountVal}\n`;
        }
      }

      // Show net amount if available
      if (
        orderData.paymentSummary.netAmount !== undefined &&
        orderData.paymentSummary.netAmount !== null
      ) {
        orderText += `*Net Amount:* LKR ${Number(
          orderData.paymentSummary.netAmount,
        ).toFixed(2)}\n`;
      }

      const needToPay =
        orderData.paymentMethod === "BankTransfer" ||
          orderData.paymentMethod === "Promo"
          ? 0
          : orderData.paymentSummary &&
            (orderData.customer || orderData.organization)
            ? orderData.paymentSummary.netAmount -
            (orderData.paymentSummary.cardPaymentAmount || 0) -
            (orderData.paymentSummary.walletPaymentAmount || 0)
            : 0;

      orderText += `*Need to Pay:* LKR ${needToPay.toFixed(2)}\n`;
    }

    // Add agent information based on order source
    if (
      orderData.orderSource === "CustomerApp" ||
      orderData.orderSource === "CustomerWeb"
    ) {
      orderText += `\n*Agent:* Website\n`;
    } else if (
      orderData.orderSource === "CallCenter" &&
      orderData.orderTakenBy?.displayName
    ) {
      orderText += `\n*Agent:* ${orderData.orderTakenBy.displayName}\n`;
    }

    try {
      await navigator.clipboard.writeText(orderText);
      toast.success("Order details copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error("Failed to copy order details");
    }
  };

  // Copy order details for WhatsApp (tab-separated format like CreateOrder)
  const copyOrderDetailsForWhatsApp = async () => {
    if (!orderData) return;

    // Get customer name based on order type
    let customerName = "";
    if (orderData.organization?.displayName) {
      customerName = orderData.organization.displayName;
    } else if (orderData.customer?.displayName) {
      customerName = orderData.customer.displayName;
    } else if (orderData.cart?.displayName) {
      customerName = orderData.cart.displayName;
    } else {
      customerName = "N/A";
    }

    // Get contact number
    let contactNumber = "";
    if (orderData.deliveryDetails?.deliveryContactPhoneNumber) {
      contactNumber = orderData.deliveryDetails.deliveryContactPhoneNumber;
    }
    else if (orderData.organization?.phoneNumber || orderData.phoneNumber) {
      contactNumber =
        orderData.organization?.phoneNumber || orderData.phoneNumber;
    } else {
      contactNumber =
        orderData.phoneNumber || orderData.customer?.phoneNumber || "N/A";
    }
    // Get delivery details
    let deliveryOrCart = "";
    let deliveryAddress = "";
    let mapLocation = "";

    if (orderData.isDeliveryOrder) {
      deliveryOrCart = "Delivery";
      deliveryAddress = orderData.deliveryDetails?.address || "N/A";

      // Try to create Google Maps link if coordinates are available
      const latitude = orderData.deliveryDetails?.coordinates?.latitude;
      const longitude = orderData.deliveryDetails?.coordinates?.longitude;

      if (latitude && longitude) {
        mapLocation = `https://www.google.com/maps?q=${latitude},${longitude}`;
      } else {
        // Fallback to address if no coordinates
        const address = orderData.deliveryDetails?.address || "N/A";
        if (address !== "N/A") {
          mapLocation = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            address,
          )}`;
        } else {
          mapLocation = "N/A";
        }
      }
    } else {
      deliveryOrCart = "Cart pick up";
      deliveryAddress = orderData.cart?.displayName || "N/A";
      mapLocation = orderData.cart?.displayName || "N/A";
    }

    // Get payment method - check for promo code first
    let paymentMethod = orderData.paymentMethod || "N/A";
    if (orderData.promoCode) {
      paymentMethod = "Promo";
    } else if (
      !orderData.isDeliveryOrder &&
      orderData.paymentMethod === "CardOnDelivery"
    ) {
      paymentMethod = "Card On Pick up";
    }

    // Get special requests
    const specialRequests =
      [orderData.deliveryDetails?.deliveryInstructions, orderData.orderNote]
        .filter(Boolean)
        .join(", ") || "";

    // Get packing note
    const packingNote = orderData.packingNote || "";

    // Get payment note
    const paymentNote = orderData.paymentNote || "";

    // Get total bill value
    const rawAmount =
      orderData.paymentSummary?.netAmount ?? orderData.totalPrice ?? 0;
    const rawAmountNumber = Number(rawAmount).toFixed(2);
    const totalBill = Number(rawAmountNumber).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    // Add (Paid) indicator for certain payment methods
    const totalBillDisplay =
      paymentMethod === "BankTransfer" || paymentMethod === "Card"
        ? `${totalBill} (Paid)`
        : totalBill;

    // Process order items by food type (use current order items or editable ones for tentative)
    const itemsToProcess = shouldShowEditFeatures
      ? (editableOrderItems || []).map((it: any) => ({
        displayName: it.displayName,
        quantity: it.quantity,
        portionSize: it.portionLabel || "Regular",
        itemType: it.itemType,
      }))
      : (orderData.orderItems || []).map((it: any) => ({
        displayName: it.displayName,
        quantity: it.quantity,
        portionSize: it.portionSize || "Regular",
        itemType: it.itemType,
      }));

    const foodTypes: {
      Meatballs: string[];
      Chicken: string[];
      Fish: string[];
      Egg: string[];
      Vegetable: string[];
      others: string[];
    } = {
      Meatballs: [],
      Chicken: [],
      Fish: [],
      Egg: [],
      Vegetable: [],
      others: [],
    };

    itemsToProcess.forEach((item: any) => {
      const itemName = item.displayName || "Unknown Item";
      const quantity = item.quantity || 0;
      const isCustomProduct = item.itemType === "CustomMenu";

      const itemString = `${itemName} ${isCustomProduct ? "(Custom)" : ""
        } - ${quantity}`.trim();

      // Categorize items based on keywords in the original display name
      const itemLower = itemName.toLowerCase();
      if (
        itemLower.includes("meat ball") ||
        itemLower.includes("meatball") ||
        itemLower.includes("meatballs") ||
        itemLower.includes("meat balls")
      ) {
        foodTypes.Meatballs.push(itemString);
      } else if (itemLower.includes("chicken")) {
        foodTypes.Chicken.push(itemString);
      } else if (itemLower.includes("fish")) {
        foodTypes.Fish.push(itemString);
      } else if (itemLower.includes("egg")) {
        foodTypes.Egg.push(itemString);
      } else if (itemLower.includes("vegetable") || itemLower.includes("veg")) {
        foodTypes.Vegetable.push(itemString);
      } else {
        // Default to others if can't categorize
        foodTypes.others.push(itemString);
      }
    });

    // Get delivery time information
    let deliveryTime = "";
    if (orderData.deliveryTimeSlot) {
      // Use only the left side of a timeslot string like "12:30-13:00"
      const slot = String(orderData.deliveryTimeSlot || "");
      deliveryTime = slot.split(/\s*-\s*/)[0] || slot;
    } else if (orderData.pickupTimeSlot) {
      // Use only the left side of a timeslot string like "12:30-13:00"
      const slot = String(orderData.pickupTimeSlot || "");
      deliveryTime = slot.split(/\s*-\s*/)[0] || slot;
    } else if (orderData.deliverAt) {
      // Handle Firestore timestamp format
      let deliverAt;
      if (
        typeof orderData.deliverAt === "object" &&
        orderData.deliverAt._seconds
      ) {
        deliverAt = formatFirestoreTimestamp(orderData.deliverAt);
      } else {
        deliverAt = formatFirestoreTimestamp(orderData.deliverAt);
      }
      deliveryTime = deliverAt.toLocaleString();
    } else {
      deliveryTime = "N/A";
    }

    // Get agent name
    let agentName = "Call center";
    if (
      orderData.orderSource === "CustomerApp" ||
      orderData.orderSource === "CustomerWeb"
    ) {
      agentName = "Website";
    } else if (orderData.orderTakenBy?.displayName) {
      agentName = orderData.orderTakenBy.displayName;
    }

    let channel = orderData?.channel;

    // Build packing list with items and custom items
    let packingListText = "Packing List\n";
    const displayedItems: any[] = shouldShowEditFeatures
      ? (editableOrderItems || []).map((it: any) => ({
        displayName: it.displayName,
        quantity: it.quantity,
        itemType: it.itemType,
        customItems: it.customItems,
        tinyId: it.tinyId,
        skuId: it.skuId,
      }))
      : (orderData.orderItems || []).map((it: any) => ({
        displayName: it.displayName,
        quantity: it.quantity,
        itemType: it.itemType,
        customItems: it.customItems,
        tinyId: it.tinyId,
        skuId: it.skuId,
      }));

    displayedItems.forEach((item: any) => {
      // Add main item with quantity and tinyId if available
      const tinyIdText = item.tinyId ? `(${item.tinyId})` : "";
      const skuIdText = item.skuId ? `[${item.skuId}]` : tinyIdText;
      packingListText += `${tinyIdText} ${skuIdText} ${item.displayName} - *${item.quantity}*\n`;

      // If item has custom items, separate them by isAddOn property
      if (item.customItems && item.customItems.length > 0) {
        // First add non-addon items (section items)
        item.customItems
          .filter((customItem: any) => !customItem.isAddOn)
          .forEach((customItem: any) => {
            const customItemTinyIdText = customItem.tinyId
              ? `*(${customItem.tinyId})* `
              : "";
            const customItemSkuIdText = customItem.skuId ? `[${customItem.skuId}]` : customItemTinyIdText;
            packingListText += `*${customItemTinyIdText} ${customItemSkuIdText} ${customItem.displayName} - *${customItem.quantity}*\n`;
          });

        // Add add-on items for this specific item immediately after section items
        const addOnItems = item.customItems.filter(
          (customItem: any) => customItem.isAddOn,
        );
        if (addOnItems.length > 0) {
          packingListText += `---- Add On Items ---- \n`;
          addOnItems.forEach((customItem: any) => {
            const customItemTinyIdText = customItem.tinyId
              ? `*(${customItem.tinyId})* `
              : "";
            const customItemSkuIdText = customItem.skuId ? `[${customItem.skuId}]` : customItemTinyIdText;
            packingListText += `*${customItemTinyIdText} ${customItemSkuIdText} ${customItem.displayName} - *${customItem.quantity}*\n`;
          });
        }
      }
    });

    // Format the clipboard text (tab-separated format)
    const clipboardText = `Order Number\t:\t${orderData.orderReference || "N/A"}
Name\t:\t${customerName}
Contact Number\t:\t${contactNumber}
Delivery or Pickup Time\t:\t${deliveryTime}
***********************
Delivery or Cart\t:\t${deliveryOrCart}
Channel\t:\t${channel || "N/A"}
Delivery Address\t:\t${deliveryAddress}
Map Location\t:\t${mapLocation}
Payment Method\t:\t${paymentMethod}
${paymentNote ? `Payment Note\t:\t${paymentNote}\n` : ''}Special Requests\t:\t${specialRequests}
Total Bill Value\t:\t${totalBillDisplay}
***********************
Chicken\t:\t${foodTypes.Chicken.join(", ") || ""}
Fish\t:\t${foodTypes.Fish.join(", ") || ""}
Egg\t:\t${foodTypes.Egg.join(", ") || ""}
Meatballs\t:\t${foodTypes.Meatballs.join(", ") || ""}
Vegetable\t:\t${foodTypes.Vegetable.join(", ") || ""}
Others\t:\t${foodTypes.others.join(", ") || ""}
***********************
${packingListText}${packingNote ? `Packing Note\t:\t${packingNote}\n` : ''}Agent\t:\t${agentName}`;

    try {
      await navigator.clipboard.writeText(clipboardText);
      toast.success("Order details copied to WhatsApp format!");
    } catch (err) {
      console.error("Failed to copy: ", err);
      toast.error("Failed to copy order details");
    }
  };

  useEffect(() => {
    if (menuListData?.MenuList) setMenus(menuListData.MenuList);
  }, [menuListData]);

  useEffect(() => {
    if (menuItemsData) {
      const items: ReduxMenuItem[] =
        menuItemsData?.flatMap((category: any) => category.items) || [];
      setSingleMenuItemsList(items);
    }
  }, [menuItemsData]);

  useEffect(() => {
    if (setMenuData?.setMenuList) setComboItemList(setMenuData.setMenuList);
  }, [setMenuData]);

  useEffect(() => {
    if (customMenuData?.customMenuList)
      setCustomMenuItemList(customMenuData.customMenuList);
  }, [customMenuData]);

  // Editable order items state seeded from current order items
  const [editableOrderItems, setEditableOrderItems] = useState<any[]>([]);
  const [editableOrderNote, setEditableOrderNote] = useState<string>("");
  const [editablePackingNote, setEditablePackingNote] = useState<string>("");
  const [editablePaymentNote, setEditablePaymentNote] = useState<string>("");
  const [isManualDeliveryFee, setIsManualDeliveryFee] = useState<boolean>(false);
  const [manualDeliveryFeeAmount, setManualDeliveryFeeAmount] = useState<number>(0);

  useEffect(() => {
    if (!orderData?.orderItems) return;
    const mapped = orderData.orderItems.map((oi: any) => ({
      id: oi.menuItemId,
      tinyId: oi.tinyId,
      skuId: oi.skuId,
      menuItemId: oi.menuItemId,
      displayName: oi.displayName,
      price: oi.unitPrice,
      unitTotalPrice: oi.unitTotalPrice,
      calculatedPrice: oi.unitPrice,
      quantity: oi.quantity,
      portionLabel: oi.portionSize || "Regular",
      itemType: oi.itemType,
      customItems: oi.customItems || [],
      perItemManualDiscount: oi.perItemManualDiscount || undefined,
    }));
    setEditableOrderItems(mapped);
  }, [orderData?.orderItems]);

  // Initialize editable notes when order data changes
  useEffect(() => {
    if (orderData) {
      setEditablePackingNote(orderData.packingNote || "");
      setEditablePaymentNote(orderData.paymentNote || "");
      setEditableOrderNote(orderData.orderNote || "");
      setIsManualDeliveryFee(orderData?.paymentSummary?.isManualDeliveryFee || false);
      setManualDeliveryFeeAmount(orderData?.paymentSummary?.deliveryFee || 0);
    }
  }, [orderData]);

  const [isCreditIssued, setIsCreditIssued] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isIssuingLoading, setIsIssuingLoading] = useState(true);

  const [issueOrder, { isLoading: isLoadingOrderIssue }] =
    useIssueOrderMutation();

  const [updateOrder, { isLoading: isUpdatingOrder }] =
    useUpdateOrderMutation();
  const [getOrderPaymentBreakdown, { isLoading: isPaymentLoading }] =
    useGetOrderPaymentBreakdownMutation();
  const [assignDeliveryPartnerToOrder, { isLoading: isAssigningDeliveryPartner }] =
    useAssignDeliveryPartnerToOrderMutation();
  const [dispatchDeliveryOrderForDeliveryPartner, { isLoading: isDispatchingDeliveryOrder }] =
    useDispatchDeliveryOrderForDeliveryPartnerMutation();

  // Real-time inventory from Firebase for order updates
  // Always use current date for inventory checks
  const inventoryDate = useMemo(() => {
    return new Date();
  }, []);

  const { data: cartSummariesData, loading: isInventoryLoading } =
    useFirebaseCartSummaries(orderData?.restaurantId || "", inventoryDate);

  const [issuePickupOrder, { isLoading: isIssuingPickupOrder }] =
    useIssuePickupOrderMutation();

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const toggleRowExpansion = (id: string) => {
    setExpandedRows((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toggleChildOrderExpansion = (childId: string) => {
    setExpandedChildOrders((prevState) => ({
      ...prevState,
      [childId]: !prevState[childId],
    }));
  };

  const handlePayment = async (paymentMethod: string) => {
    setIssuedMethod(paymentMethod);
    if (orderData?.id) {
      try {
        const deliveryCartLiveLocation = deliveryCartData?.liveLocation;
        const orderLiveLocation = orderData?.liveLocation;
        const latitude =
          deliveryCartLiveLocation?._latitude ??
          deliveryCartLiveLocation?.latitude ??
          orderLiveLocation?._latitude ??
          orderLiveLocation?.latitude ??
          0;
        const longitude =
          deliveryCartLiveLocation?._longitude ??
          deliveryCartLiveLocation?.longitude ??
          deliveryCartLiveLocation?.langitude ??
          orderLiveLocation?._longitude ??
          orderLiveLocation?.longitude ??
          orderLiveLocation?.langitude ??
          0;

        setIsIssuingLoading(true);
        await issueOrder({
          orderId: orderData.id,
          groupOrderId: orderData?.groupOrderId,
          liveLocation: { longitude, latitude },
          reason: "Complete",
          paymentMethod,
        }).unwrap();
        // Firebase will automatically update orderData via real-time listener
        setIsOrderIssued(true);
        toast.success("Order issued successfully:");
        setIsIssuingLoading(false);
      } catch (error) {
        setIsIssuingLoading(false);
        toast.error("Failed to issue order:");
      }
    }
  };

  // Calculate the need to pay amount
  // const calculateNeedToPay = useMemo(() => {
  //   if (!orderData?.paymentSummary) return 0;

  //   if (
  //     orderData.paymentMethod === "BankTransfer" ||
  //     orderData.paymentMethod === "Promo"
  //   ) {
  //     return 0;
  //   }

  //   if (orderData.customer || orderData.organization) {
  //     return (
  //       orderData.paymentSummary.netAmount -
  //       (orderData.paymentSummary.cardPaymentAmount || 0) -
  //       (orderData.paymentSummary.walletPaymentAmount || 0)
  //     );
  //   }

  //   return 0;
  // }, [orderData]);

  // Check if order status is ArrivedAtCart and it's a pickup order
  // const isArrivedAtCartPickupOrder = useMemo(() => {
  //   return orderData?.orderStatus === "ArrivedAtCart";
  // }, [orderData]);

  // Handle issue button click for pickup orders
  // const handleIssuePickupOrder = async () => {
  //   if (!orderData?.id) {
  //     toast.error("Order ID is missing");
  //     return;
  //   }

  //   const needToPay = calculateNeedToPay;

  //   // If need to pay is greater than 0, show modal to select payment method
  //   if (needToPay > 0) {
  //     setShowPickupPaymentModal(true);
  //   } else {
  //     // If no amount to pay, directly issue with default payment method
  //     try {
  //       await issuePickupOrder({
  //         orderId: orderData.id,
  //         paymentMethod: orderData?.paymentMethod || "Cash",
  //       }).unwrap();
  //       toast.success("Pickup order issued successfully!");
  //       // Firebase will automatically update orderData via real-time listener
  //     } catch (error: any) {
  //       toast.error(error?.data?.message || "Failed to issue pickup order");
  //     }
  //   }
  // };

  // Handle confirm payment method for pickup orders
  const handleConfirmPickupPayment = async () => {
    if (!orderData?.id) {
      toast.error("Order ID is missing");
      return;
    }

    try {
      await issuePickupOrder({
        orderId: orderData.id,
        paymentMethod: selectedPaymentMethod,
      }).unwrap();
      toast.success("Pickup order issued successfully!");
      setShowPickupPaymentModal(false);
      // Firebase will automatically update orderData via real-time listener
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to issue pickup order");
    }
  };

  const renderCustomMenuDetails = (item: OrderItem) => {
    if (item?.customItems && item.customItems.length > 0) {
      return (
        <div className="d-flex justify-content-between">
          {((item.unitTotalPrice ?? 0) * item.quantity).toFixed(2)}
          <Button
            size={"sm"}
            variant="primary"
            onClick={() => toggleRowExpansion(item.menuItemId)}
            aria-expanded={expandedRows[item.menuItemId]}
            aria-controls={`custom-items-${item.menuItemId}`}
          >
            <FontAwesomeIcon
              icon={expandedRows[item.menuItemId] ? faChevronUp : faChevronDown}
            />
          </Button>
        </div>
      );
    }
    return (item.unitPrice * item.quantity)?.toFixed(2);
  };

  // const deliveryCartId = useSelector((state: any) => state.restaurant.selectedRestaurantDetails.deliveryCartId || state.restaurant.data.deliveryCartId);
  // const orderCartId = orderData?.deliveryCart?.cartId || orderData?.cart?.cartId;
  // const isOrderFromRestaurant = orderData?.isDeliveryOrder && (deliveryCartId === orderCartId) && (isChildOrder || isNormalOrder);
  const disabledStatuses = [
    "PackingComplete",
    "Arrived",
    "Completed",
    "Cancelled",
    // "ReadyAtKitchen"
    // "ArrivedAtCart",
    // "IssuedByKitchen",
  ];

  const isUpdateButtonDisabled = useMemo(() => {
    if (!orderData?.orderStatus || !orderData?.orderCreatedBy) return true;
    const currentStatus = orderData.orderStatus;
    const createdBy = orderData.orderCreatedBy.toLowerCase();

    // Keep update enabled for this delivery transition case.
    if (
      currentStatus === "PackingComplete" &&
      orderData?.deliveryStatus === "OutForDelivery"
    ) {
      return false;
    }

    // Enable cancellation for parent orders (unless already completed)
    if (isParentOrder) {
      // Allow status update only if not "Completed" (so they can be cancelled)
      return currentStatus === COMPLETED_ORDER_STATUS;
    }

    // Allow child orders to update status, but with other restrictions
    if (isChildOrder) {
      // Disable if already in completed or cancelled status
      if (
        currentStatus === COMPLETED_ORDER_STATUS ||
        currentStatus === "Cancelled"
      ) {
        return true;
      }
      // For child orders created by customer, apply the same logic
      if (
        createdBy === "customer" &&
        disabledStatuses.includes(currentStatus)
      ) {
        return true;
      }
      return false;
    }

    // If created by "Customer" and status is in disabledStatuses, disable the button
    if (createdBy === "customer" && disabledStatuses.includes(currentStatus)) {
      return true;
    }

    // If created by "Cart Person", do not disable the button regardless of status
    if (createdBy === "cart person") {
      return false;
    }

    // Default to the original logic for other cases
    return disabledStatuses.includes(currentStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData, isParentOrder, isChildOrder]);

  // Add this useEffect to reset credit selection when customer data changes
  useEffect(() => {
    // Reset credit selection if customer object becomes available
    if (orderData?.customer && isCreditIssued) {
      setIsCreditIssued(false);
      // Optionally default to Card or Cash
      setIsCardIssued(true); // Default to Card when credit is removed
    }
  }, [orderData?.customer, isCreditIssued]);

  type ItemType = "MenuItem" | "SetMenu" | "CustomMenu";

  const handlePaymentMethodChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { id } = event.target;

    if (id === "isCardIssued") {
      setIsCardIssued(true);
      setIsCashIssued(false);
      setIsCreditIssued(false);
    } else if (id === "isCashIssued") {
      setIsCardIssued(false);
      setIsCashIssued(true);
      setIsCreditIssued(false);
    } else if (id === "isCreditIssued" && !orderData?.customer) {
      // Only allow Credit selection if customer is null
      setIsCardIssued(false);
      setIsCashIssued(false);
      setIsCreditIssued(true);
    }
  };

  // Function to navigate to child order view
  const navigateToChildOrder = (childOrderId: string) => {
    navigate("/orders/delivery-order/view", {
      state: { orderId: childOrderId },
    });
  };

  // Function to render child order status badge
  const renderChildOrderStatusBadge = (status: string) => {
    const getBadgeColor = (status: string) => {
      switch (status) {
        case "Pending":
          return "warning";
        case "ApprovedByRestaurant":
          return "warning";
        case "Preparing":
          return "primary";
        case "ReadyAtKitchen":
          return "dark";
        case "IssuedByKitchen":
          return "secondary";
        case "Completed":
          return "success";
        case "Cancelled":
          return "danger";
        default:
          return "secondary";
      }
    };

    const getStatusText = (status: string) => {
      return status.replace(/([A-Z])/g, " $1").trim();
    };

    return (
      <Badge color={getBadgeColor(status)} className="px-3 py-2">
        {getStatusText(status)}
      </Badge>
    );
  };

  // Function to render child order item summary
  const renderChildOrderItemsSummary = (childOrder: any) => {
    const items = childOrder.orderItems || [];
    if (items.length === 0) return "No items";

    const itemCount = items.reduce(
      (total: number, item: any) => total + (item.quantity || 0),
      0,
    );
    const itemNames = items
      .slice(0, 2)
      .map((item: any) => `${item.displayName} (x${item.quantity})`)
      .join(", ");

    return `${itemCount} items: ${itemNames}${items.length > 2 ? "..." : ""}`;
  };

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />

      {/* Print Preview Modal */}
      {previewVisible && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered"
            style={{ maxWidth: "90%" }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Print Preview</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={hidePreview}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div
                  className="print-preview-container"
                  style={{
                    width: "80mm",
                    margin: "0 auto",
                    backgroundColor: "white",
                    padding: "10px",
                    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                  }}
                >
                  <iframe
                    srcDoc={previewHTML}
                    title="Print Preview"
                    style={{
                      width: "100%",
                      height: "500px",
                      border: "1px solid #ddd",
                      backgroundColor: "white",
                    }}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={hidePreview}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const iframe = document.querySelector(
                      ".print-preview-container iframe",
                    ) as HTMLIFrameElement;
                    if (iframe && iframe.contentWindow) {
                      iframe.contentWindow.print();
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faPrint} className="me-2" />
                  Print from Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pickup Payment Method Modal */}
      <Modal
        show={showPickupPaymentModal}
        onHide={() => setShowPickupPaymentModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Payment Method</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Please select the payment method for this pickup order:</p>
          <Form>
            <Form.Check
              type="radio"
              id="cashPayment"
              label="Cash"
              name="pickupPaymentMethod"
              checked={selectedPaymentMethod === "Cash"}
              onChange={() => setSelectedPaymentMethod("Cash")}
              className="mb-2"
            />
            <Form.Check
              type="radio"
              id="cardOnDeliveryPayment"
              label="Card on Delivery"
              name="pickupPaymentMethod"
              checked={selectedPaymentMethod === "CardOnDelivery"}
              onChange={() => setSelectedPaymentMethod("CardOnDelivery")}
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPickupPaymentModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmPickupPayment}
            disabled={isIssuingPickupOrder}
          >
            {isIssuingPickupOrder ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Issuing...
              </>
            ) : (
              "Confirm & Issue"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {isLoadingOrderData ? (
        <>
          <Row>
            <Col lg="6">
              <Card className="w-100" style={{ height: 375 }}>
                <Card.Body>
                  <table className="info-table mt-4">
                    <tbody>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Card.Body>
              </Card>
            </Col>
            <Col lg="6">
              <Card className="w-100" style={{ height: 375 }}>
                <Card.Body>
                  <table className="info-table mt-4">
                    <tbody>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <hr />
                        </td>
                        <td>
                          <hr />
                        </td>
                      </tr>
                      <tr>
                        <td className="label">
                          <Placeholder xs={12} />
                        </td>
                        <td className="value mt-5 mb-5">
                          <Placeholder xs={12} />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Card className="order-card">
            <TableLoadingComponent numColumns={5} />
          </Card>
        </>
      ) : (
        <div className="order-view">
          <h5 className="header">
            Order View ( {orderData?.orderReference} ){" "} - <Button variant="outline-primary" title="View Activity" onClick={handleViewActivity}>View Activity</Button>
            {isParentOrder && <Badge color="primary">Parent Order</Badge>}
          </h5>
          <div className="d-flex justify-content-end gap-2 mb-3">

            {/* Reorder button */}
            <ButtonComponent
              titel="Reorder"
              onClick={handleReorder}
              isDisabled={!orderData}
            />

            <div className="btn-group me-3">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handlePrintRiderCopy}
                disabled={isPrinting || !orderData}
                title="Print Rider Copy"
              >
                <FontAwesomeIcon icon={faPrint} className="me-2" />
                {isPrinting ? "Printing..." : "Rider Copy"}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handlePrintCustomerCopy}
                disabled={isPrinting || !orderData}
                title="Print Customer Copy"
              >
                <FontAwesomeIcon icon={faPrint} className="me-2" />
                Customer Copy
              </Button>
            </div>


            {/* Copy buttons */}
            <Button
              variant="outline-success"
              size="sm"
              onClick={copyOrderDetailsForWhatsApp}
              title="Copy order details to distribution"
            >
              <FontAwesomeIcon icon={faCopy} className="me-2" />
              Copy to Distribution
            </Button>
            <Button
              variant="outline-success"
              size="sm"
              onClick={copyOrderDetails}
              title="Copy order details to customer"
            >
              <FontAwesomeIcon icon={faCopy} className="me-2" />
              Copy to Customer
            </Button>
            {!isParentOrder && (
              <ButtonComponent
                titel="Add New Order"
                onClick={handleAddNewOrder}
              />
            )}
          </div>
          <Row className="gx-3">
            <Col lg="6" className="d-flex flex-column">
              {/* Order Details Card */}
              <Card className="w-100 mb-3">
                <Card.Header className="bg-light">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon
                        icon={faUserTie}
                        className="me-2 text-primary"
                      />
                      <h6 className="mb-0 fw-bold">Order Details</h6>
                    </div>
                    {orderData?.isDeliveryOrder && isParentOrder && canCancelParentOrder && (
                      <Button

                        variant="danger"
                        onClick={openModal}
                        disabled={isUpdateButtonDisabled}
                        title="Cancel Parent Order"
                      >
                        Cancel Order
                      </Button>)
                    }
                  </div>
                </Card.Header>
                <CardBody>
                  <table className="info-table">
                    <tbody>
                      <tr>
                        <td className="label">Order Number</td>
                        <td className="value">: {orderData?.orderReference}</td>
                      </tr>
                      <tr>
                        <td className="label">Status</td>
                        <td className="order-status-style value">
                          :{" "}
                          <Badge
                            color={
                              orderData?.orderStatus === "Pending"
                                ? "warning"
                                : orderData?.orderStatus === "AcceptedByCart"
                                  ? "secondary"
                                  : orderData?.orderStatus ===
                                    "ApprovedByRestaurant"
                                    ? "warning"
                                    : orderData?.orderStatus === "Collecting"
                                      ? "danger"
                                      : orderData?.orderStatus === "Preparing"
                                        ? "primary"
                                        : orderData?.orderStatus ===
                                          "ReadyAtKitchen"
                                          ? "dark"
                                          : orderData?.orderStatus === "Arrived"
                                            ? "secondary"
                                            : orderData?.orderStatus ===
                                              "Completed"
                                              ? "success"
                                              : orderData?.orderStatus ===
                                                "IssuedByKitchen"
                                                ? "secondary"
                                                : orderData?.orderStatus ===
                                                  "PackingComplete"
                                                  ? "primary"
                                                  : orderData?.orderStatus ===
                                                    "Cancelled"
                                                    ? "danger"
                                                    : orderData?.orderStatus ===
                                                      "Tentative"
                                                      ? "secondary"
                                                      : "secondary"
                            }
                            className="px-3 py-2"
                          >
                            <div style={{ fontSize: 12 }}>
                              {orderData?.orderStatus === "Pending"
                                ? "Pending"
                                : orderData?.orderStatus === "AcceptedByCart"
                                  ? "Accepted By Cart"
                                  : orderData?.orderStatus ===
                                    "ApprovedByRestaurant"
                                    ? "Approved By Restaurant"
                                    : orderData?.orderStatus === "Collecting"
                                      ? "Collecting"
                                      : orderData?.orderStatus === "Preparing"
                                        ? "Preparing"
                                        : orderData?.orderStatus ===
                                          "ReadyAtKitchen"
                                          ? "Ready At Kitchen"
                                          : orderData?.orderStatus ===
                                            "ArrivedAtCart"
                                            ? "Arrived At Cart"
                                            : orderData?.orderStatus ===
                                              COMPLETED_ORDER_STATUS
                                              ? "Completed"
                                              : orderData?.orderStatus ===
                                                "InAppCart"
                                                ? "In App Cart"
                                                : orderData?.orderStatus ===
                                                  ISSUED_BY_KITCHEN_ORDER_STATUS
                                                  ? "Issued By Kitchen"
                                                  : orderData?.orderStatus ===
                                                    "Tentative"
                                                    ? "Tentative"
                                                    : orderData?.orderStatus ===
                                                      "PackingComplete"
                                                      ? "Packing Complete"
                                                      : "Cancelled"}
                            </div>
                          </Badge>
                          {/* {!((isChildOrder || isNormalOrder) && !isOrderFromRestaurant) && (
                            isParentOrder ? ( */}
                          {isParentOrder || orderData?.orderStatus === "Completed" || orderData?.orderStatus === "Cancelled" || orderData?.orderStatus === "InAppCart" ? (
                            <></>
                          ) : (
                            <Button
                              className="status-button-style"
                              variant="danger"
                              onClick={openModal}
                              disabled={isUpdateButtonDisabled}
                              title="Update Status"
                            >
                              <FontAwesomeIcon icon={faRightLeft} />
                            </Button>
                          )}
                        </td>
                      </tr>
                      {orderData?.deliveryStatus && (
                        <tr>
                          <td className="label">Delivery Status</td>
                          <td className="value">
                            : {orderData?.deliveryStatus || "N/A"}
                          </td>
                        </tr>
                      )}
                      {/* {orderData?.isDeliveryOrder && orderData?.orderStatus === "PackingComplete" && mainCartData?.cartType !== "Moving" && ( */}
                      {/* Gayashan Informed that Delivery Route Optimizor need to show in any order status */}
                      {orderData?.isDeliveryOrder && mainCartData?.cartType !== "Moving" && (
                        <>
                          <tr>
                            {orderData?.deliveryCart || (orderData?.orderStatus === "PackingComplete" && orderData?.deliveryStatus === "PackingComplete") ? (
                              <td className="label">Delivery Partner</td>
                            ) : null}
                            <td className="value">
                              {orderData?.deliveryCart && !isChangingDeliveryPartner ? (
                                <div className="d-flex align-items-center gap-2">
                                  <span>: {orderData.deliveryCart.displayName}</span>
                                  {orderData?.deliveryStatus !== "Dispatched" && orderData?.deliveryStatus !== "OutForDelivery" && orderData?.deliveryStatus !== "Delivered" && (
                                    <div className="d-flex align-items-center gap-2">
                                      <Button size="sm" onClick={handleChangeDeliveryPartnerClick}>Change</Button>
                                      <Button size="sm" onClick={handleDispatchOrder} disabled={isDispatchingDeliveryOrder}>
                                        {isDispatchingDeliveryOrder ? (
                                          <>
                                            <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                            Dispatching...
                                          </>
                                        ) : (
                                          "Dispatch Order"
                                        )}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              ) : orderData?.orderStatus === "PackingComplete" && orderData?.deliveryStatus === "PackingComplete" && !isChangingDeliveryPartner ? (
                                <>
                                  <div className="d-flex align-items-center gap-2">
                                    <span>:</span>
                                    <Select
                                      value={selectedDeliveryPartner}
                                      onChange={setSelectedDeliveryPartner}
                                      options={deliveryPartnerOptions}
                                      placeholder="Select Delivery Partner"
                                      isSearchable
                                      className="flex-grow-1"
                                      styles={{ container: (base) => ({ ...base, minWidth: '200px' }) }}
                                    />
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={handleAssignDeliveryPartner}
                                      disabled={!selectedDeliveryPartner || isAssigningDeliveryPartner}
                                    >
                                      {isAssigningDeliveryPartner ? (
                                        <>
                                          <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                          Assigning...
                                        </>
                                      ) : (
                                        "Assign"
                                      )}
                                    </Button>
                                  </div>
                                </>
                              ) : null}
                            </td>
                          </tr>
                          {orderData?.deliveryCart?.deliveryPersonData && (
                            <tr>
                              <td className="label">Driver Contact Number</td>
                              <td className="value">
                                : {deliveryPersonData?.phoneNumber || "N/A"}
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                      {orderData?.orderNote && (
                        <tr>
                          <td className="label">Order Note</td>
                          <td className="value">
                            : {orderData?.orderNote || "N/A"}
                          </td>
                        </tr>
                      )}
                      {orderData?.packingNote && (
                        <tr>
                          <td className="label">Packing Note</td>
                          <td className="value">
                            : {orderData?.packingNote || "N/A"}
                          </td>
                        </tr>
                      )}
                      {orderData?.paymentNote && (
                        <tr>
                          <td className="label">Payment Note</td>
                          <td className="value">
                            : {orderData?.paymentNote || "N/A"}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td className="label">Created Time</td>
                        <td className="value">
                          : {formatFirestoreTimestamp(orderData?.createdAt)}
                        </td>
                      </tr>
                      {orderData?.pickupTimeSlot && (
                        <tr>
                          <td className="label">Pickup Time Slot</td>
                          <td className="value">
                            : {orderData?.pickupTimeSlot}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td className="label">Order Created By</td>
                        <td className="value">: {orderData?.orderCreatedBy}</td>
                      </tr>
                      <tr>
                        <td className="label">Order Created For</td>
                        <td className="value">
                          :{" "}
                          {orderData?.organization ? (
                            <>
                              <FontAwesomeIcon icon={faBuildingUn} />{" "}
                              &nbsp;Organization
                            </>
                          ) : orderData?.customer ? (
                            <>
                              <FontAwesomeIcon icon={faUserTie} />{" "}
                              &nbsp;Customer
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faStore} /> &nbsp; Cart
                            </>
                          )}
                        </td>
                      </tr>
                      {orderData?.cart && orderData?.customer && (
                        <tr>
                          <td className="label">Cart</td>
                          <td className="value">
                            {" "}
                            : <FontAwesomeIcon icon={faStore} /> &nbsp;
                            {orderData?.cart?.displayName}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td className="label">
                          {orderData?.orderCreatedBy?.toLowerCase() ===
                            "customer"
                            ? "Customer Name"
                            : orderData?.orderCreatedBy?.toLowerCase() ===
                              "cartperson"
                              ? "Cart Name"
                              : orderData?.orderCreatedBy?.toLowerCase() ===
                                "restaurant" &&
                                orderData?.customer?.displayName
                                ? "Customer Name"
                                : orderData?.orderCreatedBy?.toLowerCase() ===
                                  "restaurant" &&
                                  orderData?.organization?.displayName
                                  ? "Organization Name"
                                  : orderData?.orderCreatedBy?.toLowerCase() ===
                                    "restaurant" &&
                                    orderData?.cart?.displayName
                                    ? "Cart Name"
                                    : "Not Assigned"}
                        </td>
                        <td className="value">
                          :{" "}
                          {orderData?.orderCreatedBy?.toLowerCase() ===
                            "customer" && orderData?.customer?.displayName
                            ? orderData?.customer?.displayName
                            : orderData?.orderCreatedBy?.toLowerCase() ===
                              "cartperson" && orderData?.cart?.displayName
                              ? orderData?.cart?.displayName
                              : orderData?.orderCreatedBy?.toLowerCase() ===
                                "restaurant" &&
                                orderData?.organization?.displayName
                                ? orderData?.organization?.displayName
                                : orderData?.orderCreatedBy?.toLowerCase() ===
                                  "restaurant" &&
                                  orderData?.cart?.displayName &&
                                  !orderData?.customer?.displayName
                                  ? orderData?.cart?.displayName
                                  : (orderData?.customer?.displayName ??
                                    "Not Assigned")}
                        </td>
                      </tr>
                      {!orderData?.isDeliveryOrder && orderData?.phoneNumber && (
                        <tr>
                          <td className="label">Customer Contact Number</td>
                          <td className="value">: {orderData?.phoneNumber || orderData?.customer?.phoneNumber} </td>
                        </tr>
                      )}
                      {orderData?.updateNote && (
                        <tr>
                          <td className="label">Update Note</td>
                          <td className="value">: {orderData?.updateNote}</td>
                        </tr>
                      )}
                      {orderData?.channel && (
                        <tr>
                          <td className="label">Channel</td>
                          <td className="value">: {orderData?.channel}</td>
                        </tr>
                      )}
                      {orderData?.orderTakenBy && (
                        <tr>
                          <td className="label">Order Taken By</td>
                          <td className="value">
                            : {orderData?.orderTakenBy?.displayName}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </CardBody>
              </Card>

              {/* Delivery Details Card - Only show for delivery orders */}
              {orderData?.isDeliveryOrder && (
                <Card className="w-100">
                  <Card.Header className="bg-light">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon
                          icon={faTruck}
                          className="me-2 text-primary"
                        />
                        <h6 className="mb-0 fw-bold">Delivery Details</h6>
                      </div>
                      <div>
                        {orderData?.cart?.cartId &&
                          orderData?.orderStatus !== "Cancelled" &&
                          orderData?.orderStatus !== "Completed" && (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleDriverLocationClick()}
                            >
                              <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                              Track Order
                            </Button>
                          )}
                      </div>
                    </div>
                  </Card.Header>
                  <CardBody>
                    <table className="info-table">
                      <tbody>
                        {orderData?.isSplitOrder && orderData?.parentOrderId && (<tr>
                          <td className="label"><FontAwesomeIcon
                            icon={faMotorcycle}
                            className="me-2"
                          />Delivery Cart</td>
                          <td className="value">
                            :{" "}
                            {orderData?.deliveryCart?.displayName}
                          </td>
                        </tr>)}
                        <tr>
                          <td className="label"><FontAwesomeIcon
                            icon={faClock}
                            className="me-2"
                          />Deliver Time Range</td>
                          <td className="value">
                            :{" "}
                            {orderData?.deliveryTimeSlot
                              ? orderData?.deliveryTimeSlot
                              : formatFirestoreTimestamp(orderData?.deliverAt)}
                            {"  "}
                            {orderData?.isScheduledOrder && (
                              <FontAwesomeIcon
                                className="red-icon-with-space"
                                icon={faCalendarAlt}
                              />
                            )}
                          </td>
                        </tr>
                        {orderData?.deliveryDetails?.address && (
                          <tr>
                            <td className="label">
                              <FontAwesomeIcon
                                icon={faMapMarkerAlt}
                                className="me-2"
                              />
                              Delivery Address
                            </td>
                            <td className="value">
                              : {orderData?.deliveryDetails?.address}
                            </td>
                          </tr>
                        )}
                        {orderData?.deliveryDetails?.deliveryContactName && (
                          <tr>
                            <td className="label">
                              <FontAwesomeIcon icon={faUser} className="me-2" />
                              Delivery Contact Name
                            </td>
                            <td className="value">
                              :{" "}
                              {orderData?.deliveryDetails?.deliveryContactName}
                            </td>
                          </tr>
                        )}
                        {orderData?.deliveryDetails
                          ?.deliveryContactPhoneNumber && (
                            <tr>
                              <td className="label">
                                <FontAwesomeIcon
                                  icon={faPhone}
                                  className="me-2"
                                />
                                Delivery Contact Number
                              </td>
                              <td className="value">
                                :{" "}
                                {
                                  orderData?.deliveryDetails
                                    ?.deliveryContactPhoneNumber
                                }
                              </td>
                            </tr>
                          )}
                        {orderData?.deliveryDetails?.deliveryInstructions && (
                          <tr>
                            <td className="label">Delivery Instructions</td>
                            <td className="value">
                              :{" "}
                              {orderData?.deliveryDetails?.deliveryInstructions}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </CardBody>
                </Card>
              )}
            </Col>

            <Col lg="6" className="d-flex flex-column">
              <Card className="w-100 flex-grow-1">
                <CardBody className="d-flex flex-column h-100">
                  <div className="flex-grow-1">
                    {orderData &&
                      orderData.orderStatus === PACKING_COMPLETED_ORDER_STATUS &&
                      !isOrderIssued &&
                      !isParentOrder &&
                      orderData?.deliveryStatus !== "OutForDelivery" && (
                        <>
                          {canIssueOrders ? (
                            <>
                              {/* Check if cart has delivery person - for BOTH delivery and non-delivery orders */}
                              {!hasCartDeliveryPerson ? (
                                <div className="alert alert-warning text-center">
                                  <FontAwesomeIcon
                                    icon={faUserTie}
                                    className="me-2"
                                  />
                                  <strong>Delivery Person Not Assigned</strong>
                                  <br />
                                  <small>
                                    This cart is not assigned to a delivery
                                    person. Please assign a delivery person
                                    before issuing the order.
                                  </small>
                                </div>
                              ) : (
                                <>
                                  {orderData.isDeliveryOrder ? (
                                    <div className="d-flex align-items-center">
                                      <OutlineButtonComponent
                                        titel={"Issue"}
                                        icon={
                                          isLoadingOrderIssue ? (
                                            <FontAwesomeIcon
                                              icon={faSpinner}
                                              spin
                                            />
                                          ) : (
                                            <FontAwesomeIcon icon={faShare} />
                                          )
                                        }
                                        colour="primary"
                                        onClick={() => handlePayment("Credit")}
                                        isDisabled={isLoadingOrderIssue}
                                      />
                                    </div>
                                  ) : (
                                    <div className="d-flex align-items-center gap-3">
                                      <h6 className="mb-0 me-3">
                                        Please Select a Payment Method
                                      </h6>

                                      {/* Card */}
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="radio"
                                          name="paymentMethod"
                                          id="isCardIssued"
                                          checked={isCardIssued}
                                          onChange={handlePaymentMethodChange}
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor="isCardIssued"
                                        >
                                          <FontAwesomeIcon
                                            icon={faCreditCard}
                                          />{" "}
                                          &nbsp;Card
                                        </label>
                                      </div>

                                      {/* Cash */}
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="radio"
                                          name="paymentMethod"
                                          id="isCashIssued"
                                          checked={isCashIssued}
                                          onChange={handlePaymentMethodChange}
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor="isCashIssued"
                                        >
                                          <FontAwesomeIcon icon={faCoins} />{" "}
                                          &nbsp;Cash
                                        </label>
                                      </div>

                                      {/* Credit (only when no customer) */}
                                      {!orderData.customer && (
                                        <div className="form-check">
                                          <input
                                            className="form-check-input"
                                            type="radio"
                                            name="paymentMethod"
                                            id="isCreditIssued"
                                            checked={isCreditIssued}
                                            onChange={handlePaymentMethodChange}
                                          />
                                          <label
                                            className="form-check-label"
                                            htmlFor="isCreditIssued"
                                          >
                                            <FontAwesomeIcon
                                              icon={faMoneyCheckAlt}
                                            />{" "}
                                            &nbsp;Credit
                                          </label>
                                        </div>
                                      )}

                                      {/* Issue Button */}
                                      <div className="ms-auto">
                                        <OutlineButtonComponent
                                          titel={"Issue"}
                                          icon={
                                            isLoadingOrderIssue ? (
                                              <FontAwesomeIcon
                                                icon={faSpinner}
                                                spin
                                              />
                                            ) : (
                                              <FontAwesomeIcon icon={faShare} />
                                            )
                                          }
                                          colour="primary"
                                          onClick={() =>
                                            handlePayment(
                                              isCashIssued
                                                ? "Cash"
                                                : isCardIssued
                                                  ? "Card"
                                                  : "Credit",
                                            )
                                          }
                                          isDisabled={
                                            isLoadingOrderIssue ||
                                            (!orderData.paymentCompleted &&
                                              !(
                                                isCardIssued ||
                                                isCashIssued ||
                                                (!orderData.customer &&
                                                  isCreditIssued)
                                              ))
                                          }
                                        />
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </>
                          ) : (
                            <div className="alert alert-warning text-center">
                              <FontAwesomeIcon
                                icon={faShare}
                                className="me-2"
                              />
                              <strong>Order Issue Restricted</strong>
                              <br />
                              <small>
                                Your role ({userRole}) doesn't have permission
                                to issue orders. Please contact an administrator
                                to complete this action.
                              </small>
                            </div>
                          )}
                        </>
                      )}
                    <br />
                    {orderData?.isDeliveryOrder && (
                      <div
                        className={
                          orderData &&
                            orderData.orderStatus ===
                            READY_AT_KITCHEN_ORDER_STATUS
                            ? "info-row"
                            : "info-row-adjust"
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <span className="label-value">Delivery Fee</span>
                            {orderData?.paymentSummary?.isDeliveryFree && (
                              <span
                                style={{
                                  display: "inline-block",
                                  backgroundColor: "#e6f4ea",
                                  color: "#28a745",
                                  fontSize: 12,
                                  padding: "0.15rem 0.45rem",
                                  borderRadius: 6,
                                  verticalAlign: "middle",
                                }}
                              >
                                Free
                              </span>
                            )}
                          </div>

                          <div>
                            {orderData?.paymentSummary?.isDeliveryFree ? (
                              <span
                                style={{
                                  color: "#28a745",
                                  textDecoration: "line-through",
                                  fontWeight: 600,
                                }}
                              >
                                : LKR{" "}
                                {(
                                  orderData?.paymentSummary?.deliveryFee ?? 0
                                ).toFixed(2)}
                              </span>
                            ) : (
                              <span className="t-value">
                                : LKR{" "}
                                {(
                                  orderData?.paymentSummary?.deliveryFee ?? 0
                                ).toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    <div
                      className={
                        orderData &&
                          orderData.orderStatus === READY_AT_KITCHEN_ORDER_STATUS
                          ? "info-row"
                          : "info-row-adjust"
                      }
                    >
                      <span className="label-value">Sub Total</span>

                      <span className="t-value">
                        : LKR{" "}
                        {orderData?.paymentSummary?.totalPrice?.toFixed(2) ??
                          orderData?.totalPrice?.toFixed(2)}
                      </span>
                    </div>
                    {orderData?.paymentSummary?.discount !== 0 && (
                      <div
                        className={
                          orderData &&
                            orderData.orderStatus ===
                            READY_AT_KITCHEN_ORDER_STATUS
                            ? "info-row"
                            : "info-row-adjust"
                        }
                      >
                        <span className="label-value">Discounts</span>
                        <span className="t-value">
                          : LKR{" "}
                          {orderData?.paymentSummary?.discount?.toFixed(2) ??
                            orderData?.paymentSummary?.discount?.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div
                      className={
                        orderData &&
                          orderData.orderStatus === READY_AT_KITCHEN_ORDER_STATUS
                          ? "info-row"
                          : "info-row-adjust"
                      }
                    >
                      <span className="label-value">Payment Method</span>

                      <span className="t-value">
                        :{" "}
                        {orderData
                          ? orderData.promoCode
                            ? "Promo"
                            : orderData.paymentMethod === "CashPlusWallet"
                              ? "Cash & Wallet"
                              : orderData.paymentMethod === "CardPlusWallet"
                                ? "Card & Wallet"
                                : !orderData.isDeliveryOrder &&
                                  orderData.paymentMethod === "CardOnDelivery"
                                  ? "Card On Pick up"
                                  : orderData.paymentMethod
                          : ""}
                      </span>
                    </div>
                    <div
                      className={
                        orderData &&
                          orderData.orderStatus === READY_AT_KITCHEN_ORDER_STATUS
                          ? "info-row"
                          : "info-row-adjust"
                      }
                    >
                      <span className="label-value">Paid by wallet</span>
                      <span className="t-value">
                        : LKR{" "}
                        {orderData?.paymentSummary?.walletPaymentAmount?.toFixed(
                          2,
                        ) ?? ""}
                      </span>
                    </div>
                    <div
                      className={
                        orderData &&
                          orderData.orderStatus === READY_AT_KITCHEN_ORDER_STATUS
                          ? "info-row"
                          : "info-row-adjust"
                      }
                    >
                      <span className="label-value">Paid by Card</span>
                      <span className="t-value">
                        : LKR{" "}
                        {orderData?.paymentSummary?.cardPaymentAmount?.toFixed(
                          2,
                        ) ?? ""}
                      </span>
                    </div>
                    {!orderData?.isDeliveryOrder && (
                      <div
                        className={
                          orderData &&
                            orderData.orderStatus ===
                            READY_AT_KITCHEN_ORDER_STATUS
                            ? "info-row"
                            : "info-row-adjust"
                        }
                      >
                        <span className="label-value">Cart commission</span>
                        <span className="t-value">
                          : LKR{" "}
                          {orderData?.paymentSummary?.cartCommission.toFixed(
                            2,
                          ) ?? "0.00"}
                        </span>
                      </div>
                    )}
                    <div
                      className={
                        orderData &&
                          orderData.orderStatus === READY_AT_KITCHEN_ORDER_STATUS
                          ? "info-row"
                          : "info-row-adjust"
                      }
                    >
                      <span className="label-value">Cash Payment amount</span>
                      <span className="t-value">
                        : LKR{" "}
                        {orderData?.paymentSummary?.cashPaymentAmount.toFixed(
                          2,
                        ) ?? "0.00"}
                      </span>
                    </div>
                    <hr />

                    <div
                      className={
                        orderData &&
                          orderData.orderStatus === READY_AT_KITCHEN_ORDER_STATUS
                          ? "info-row"
                          : "info-row-adjust"
                      }
                    >
                      <h5 className="label-value">Need to Pay</h5>

                      <h5 className="t-value text-danger">
                        : LKR{" "}
                        {orderData?.paymentMethod === "BankTransfer"
                          ? "0.00"
                          : orderData?.paymentSummary &&
                            (orderData.customer || orderData.organization)
                            ? (
                              orderData.paymentSummary.netAmount -
                              (orderData.paymentSummary.cardPaymentAmount ||
                                0) -
                              (orderData.paymentSummary.walletPaymentAmount ||
                                0)
                            ).toFixed(2)
                            : "0.00"}
                      </h5>
                    </div>

                    {/* Show Issue button for ArrivedAtCart pickup orders */}
                    {/* {isArrivedAtCartPickupOrder && (
                      <div className="d-flex justify-content-center mt-3">
                        <Button
                          variant="primary"
                          size="lg"
                          onClick={handleIssuePickupOrder}
                          disabled={isIssuingPickupOrder}
                        >
                          {isIssuingPickupOrder ? (
                            <>
                              <FontAwesomeIcon
                                icon={faSpinner}
                                spin
                                className="me-2"
                              />
                              Issuing...
                            </>
                          ) : (
                            "Issue"
                          )}
                        </Button>
                      </div>
                    )} */}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Child Orders Section - Only show for parent orders */}
          {isParentOrder && (
            <Card className="order-card mt-3">
              <CardHeader className="order-card-header bg-info text-white">
                <div className="">
                  <Badge color="light" className="text-info">
                    <h3>Child Orders</h3>
                  </Badge>
                </div>
              </CardHeader>
              <CardBody>
                {isLoadingChildOrders ? (
                  <div className="text-center py-4">
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    <p className="mt-2">Loading child orders...</p>
                  </div>
                ) : childOrders.length === 0 ? (
                  <div className="text-center py-4">
                    <FontAwesomeIcon
                      icon={faBox}
                      size="2x"
                      className="text-muted mb-2"
                    />
                    <p className="text-muted">No child orders found</p>
                  </div>
                ) : (
                  <Accordion defaultActiveKey="">
                    {childOrders.map((childOrder: any, index: number) => (
                      <Accordion.Item
                        eventKey={index.toString()}
                        key={childOrder.id}
                      >
                        <Accordion.Header>
                          <div className="d-flex align-items-center w-100">
                            <div className="me-3">
                              <FontAwesomeIcon
                                icon={faReceipt}
                                className="text-primary"
                              />
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <strong>{childOrder.orderReference}</strong>
                                  <span className="ms-3 text-muted">
                                    <FontAwesomeIcon
                                      icon={faStore}
                                      className="me-1"
                                    />
                                    {childOrder.cart?.displayName || "No Cart"}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                  {renderChildOrderStatusBadge(
                                    childOrder.orderStatus,
                                  )}
                                  <Badge bg="secondary">
                                    LKR{" "}
                                    {(
                                      childOrder.paymentSummary?.netAmount ||
                                      childOrder.totalPrice ||
                                      0
                                    ).toFixed(2)}
                                  </Badge>
                                  <div>
                                    {childOrder.isDeliveryOrder &&
                                      (childOrder.cart?.cartId || childOrder.deliveryCart?.cartId) &&
                                      childOrder.orderStatus !== "Cancelled" &&
                                      childOrder.orderStatus !== "Completed" && (
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={() => handleDriverLocationClick(childOrder)}
                                          className="me-2"
                                        >
                                          <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                                          Track Order
                                        </Button>
                                      )}
                                  </div>
                                </div>
                              </div>
                              <div className="text-muted small mt-1">
                                {renderChildOrderItemsSummary(childOrder)}
                              </div>
                            </div>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <div className="child-order-details">
                            <Row>
                              <Col md="6">
                                <table className="table table-sm">
                                  <tbody>
                                    <tr>
                                      <th>Order Reference:</th>
                                      <td>{childOrder.orderReference}</td>
                                    </tr>
                                    <tr>
                                      <th>Cart:</th>
                                      <td>
                                        {childOrder.cart?.displayName || "N/A"}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th>Status:</th>
                                      <td>
                                        {renderChildOrderStatusBadge(
                                          childOrder.orderStatus,
                                        )}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th>Created:</th>
                                      <td>
                                        {formatFirestoreTimestamp(
                                          childOrder.createdAt,
                                        )}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </Col>
                              <Col md="6">
                                <table className="table table-sm">
                                  <tbody>
                                    <tr>
                                      <th>Subtotal:</th>
                                      <td>
                                        LKR{" "}
                                        {childOrder.paymentSummary?.totalPrice?.toFixed(
                                          2,
                                        ) ||
                                          childOrder.totalPrice?.toFixed(2) ||
                                          "0.00"}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th>Delivery Fee:</th>
                                      <td>
                                        LKR{" "}
                                        {childOrder.paymentSummary?.deliveryFee?.toFixed(
                                          2,
                                        ) || "0.00"}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th>Net Amount:</th>
                                      <td>
                                        LKR{" "}
                                        {childOrder.paymentSummary?.netAmount?.toFixed(
                                          2,
                                        ) ||
                                          childOrder.totalPrice?.toFixed(2) ||
                                          "0.00"}
                                      </td>
                                    </tr>
                                    <tr>
                                      <th>Payment Method:</th>
                                      <td>
                                        {childOrder.paymentMethod || "N/A"}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </Col>
                            </Row>

                            {/* Order Items */}
                            <div className="mt-3">
                              <h6 className="mb-2">Order Items:</h6>
                              <Table striped bordered size="sm">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Item Name</th>
                                    <th>Qty</th>
                                    <th>Price (LKR)</th>
                                    <th>Total (LKR)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(childOrder.orderItems || []).map(
                                    (item: any, itemIndex: number) => (
                                      <tr key={itemIndex}>
                                        <td>{itemIndex + 1}</td>
                                        <td>{item.displayName} ({item?.tinyId} ({item?.skuID}))</td>
                                        <td>{item.quantity}</td>
                                        <td>{item.unitPrice?.toFixed(2)}</td>
                                        <td>
                                          {(
                                            item.unitPrice * item.quantity
                                          )?.toFixed(2)}
                                        </td>
                                      </tr>
                                    ),
                                  )}
                                </tbody>
                                <tfoot>
                                  <tr>
                                    <td colSpan={4} className="text-end">
                                      <strong>Total:</strong>
                                    </td>
                                    <td>
                                      <strong>
                                        LKR{" "}
                                        {childOrder.totalPrice?.toFixed(2) ||
                                          "0.00"}
                                      </strong>
                                    </td>
                                  </tr>
                                </tfoot>
                              </Table>
                            </div>

                            <div className="d-flex justify-content-end mt-3">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() =>
                                  navigateToChildOrder(childOrder.id)
                                }
                              >
                                <FontAwesomeIcon
                                  icon={faEye}
                                  className="me-1"
                                />
                                View Full Details
                              </Button>
                            </div>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                )}
              </CardBody>
            </Card>
          )}

          {/* Original Order Items Card */}
          <Card className="order-card">
            <CardHeader className="order-card-header">
              <div className="d-flex align-items-center justify-content-between w-100">
                <div className="d-flex align-items-center gap-2">
                  <h6 className="mb-0">Order Items</h6>
                  {isParentOrder && (
                    <Badge color="info" className="ms-2">
                      Parent Order Summary
                    </Badge>
                  )}
                </div>
                <div className="d-flex gap-2">
                  {shouldShowEditFeatures && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setIsEditItemsOpen(true)}
                    >
                      Edit Order Items
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {(() => {
                const displayedItems: any[] = shouldShowEditFeatures
                  ? (editableOrderItems || []).map((it: any) => ({
                    menuItemId: it.id,
                    tinyId: it.tinyId,
                    skuId: it.skuId,
                    displayName: it.displayName,
                    unitPrice: it.calculatedPrice ?? it.price ?? 0,
                    quantity: it.quantity ?? 0,
                    itemType: it.itemType,
                    portionSize: it.portionLabel,
                    customItems: it.customItems,
                    unitTotalPrice:
                      it.itemType === "CustomMenu"
                        ? it.unitTotalPrice
                        : undefined,
                    perItemManualDiscount:
                      it.perItemManualDiscount || undefined,
                  }))
                  : orderData?.orderItems || [];
                const sortedItems = [...displayedItems].sort(
                  (a: any, b: any) => {
                    const orderPriority: { [key in ItemType]: number } = {
                      MenuItem: 1,
                      SetMenu: 2,
                      CustomMenu: 3,
                    };
                    return (
                      orderPriority[a.itemType as ItemType] -
                      orderPriority[b.itemType as ItemType]
                    );
                  },
                );

                const expandedRowsRenderer = (row: OrderItem) => {
                  if (
                    row.itemType === "CustomMenu" &&
                    row?.customItems &&
                    expandedRows[row.menuItemId]
                  ) {
                    // Separate section items and add-on items
                    const sectionItems = row.customItems.filter(
                      (item: any) => !item.isAddOn,
                    );
                    const addOnItems = row.customItems.filter(
                      (item: any) => item.isAddOn,
                    );

                    return (
                      <>
                        {/* Render section items */}
                        {sectionItems.map((customItem: any, idx: number) => (
                          <tr
                            key={`${row.menuItemId}-section-${idx}`}
                            className="custom-item-row-styles"
                            style={{ backgroundColor: "grey" }}
                          >
                            <td>*</td>
                            <td>
                              <div>
                                &nbsp;&nbsp;&nbsp;{customItem.displayName}&nbsp;&nbsp;&nbsp;{customItem?.tinyId && <span className="text-muted">({customItem?.tinyId})</span>}
                              </div>
                            </td>
                            <td>{customItem.unitPrice?.toFixed(2)}</td>
                            <td></td>
                            <td></td>
                            <td align={"right"}>{customItem.quantity}</td>
                          </tr>
                        ))}

                        {/* Render Add On Items separator and items if there are any */}
                        {addOnItems.length > 0 && (
                          <>
                            <tr
                              key={`${row.menuItemId}-addon-header`}
                              className="custom-item-row-styles"
                              style={{
                                backgroundColor: "#d3d3d3",
                                fontWeight: "bold",
                              }}
                            >
                              <td></td>
                              <td colSpan={5}>
                                <div>
                                  &nbsp;&nbsp;&nbsp;---- Add On Items ----
                                </div>
                              </td>
                            </tr>
                            {addOnItems.map((customItem: any, idx: number) => (
                              <tr
                                key={`${row.menuItemId}-addon-${idx}`}
                                className="custom-item-row-styles"
                                style={{ backgroundColor: "grey" }}
                              >
                                <td>*</td>
                                <td>
                                  <div>
                                    &nbsp;&nbsp;&nbsp;{customItem.displayName}&nbsp;&nbsp;&nbsp;{customItem?.tinyId && <span className="text-muted">({customItem?.tinyId})</span>}
                                  </div>
                                </td>
                                <td>{customItem.unitPrice?.toFixed(2)}</td>
                                <td></td>
                                <td></td>
                                <td align={"right"}>{customItem.quantity}</td>
                              </tr>
                            ))}
                          </>
                        )}
                      </>
                    );
                  }
                  return null;
                };

                const hasAnyDiscount = sortedItems.some(
                  (item: any) =>
                    item.perItemManualDiscount &&
                    item.perItemManualDiscount > 0,
                );

                return (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item Name</th>
                        <th>Tiny Id</th>
                        <th>Per Item Price (LKR)</th>
                        <th>Item Type</th>
                        <th>Portion Size</th>
                        <th>Quantity</th>
                        {hasAnyDiscount && <th>Discount (LKR)</th>}
                        <th>Total (LKR)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedItems.map((item: any, index: number) => {
                        const itemDiscount = item.perItemManualDiscount || 0;
                        const itemPrice = item.unitPrice || 0;
                        const quantity = item.quantity || 0;
                        const totalBeforeDiscount = itemPrice * quantity;
                        const totalDiscount = itemDiscount * quantity;
                        const finalTotal = totalBeforeDiscount - totalDiscount;

                        return (
                          <React.Fragment key={item.menuItemId || index}>
                            <tr className="custom-item-row border-none">
                              <td>{index + 1} .</td>
                              <td>
                                {item.displayName}
                                {item.itemType === "CustomMenu" && " (Custom)"} {item?.skuId && <span className="text-muted">({item.skuId})</span>}
                              </td>
                              <td> {item?.tinyId && <span> {item.tinyId}</span>}</td>
                              <td>{(item.unitPrice || 0).toFixed(2)}</td>
                              <td>
                                {item.itemType?.replace?.(
                                  /([a-z])([A-Z])/g,
                                  "$1 $2",
                                ) || ""}
                              </td>
                              <td>
                                {item.portionSize
                                  ? capitalizeFirstLetter(item.portionSize)
                                  : "N/A"}
                              </td>
                              <td>{item.quantity}</td>
                              {hasAnyDiscount && (
                                <td>
                                  {itemDiscount > 0 ? (
                                    <span className="text-danger">
                                      {itemDiscount.toFixed(2)}
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                              )}
                              <td>
                                {item.itemType === "CustomMenu" ? (
                                  renderCustomMenuDetails(item)
                                ) : itemDiscount > 0 ? (
                                  <>
                                    <span
                                      style={{
                                        textDecoration: "line-through",
                                        color: "#999",
                                        marginRight: "8px",
                                      }}
                                    >
                                      {totalBeforeDiscount.toFixed(2)}
                                    </span>
                                    <span className="text-success fw-bold">
                                      {finalTotal.toFixed(2)}
                                    </span>
                                  </>
                                ) : (
                                  finalTotal.toFixed(2)
                                )}
                              </td>
                            </tr>
                            {expandedRowsRenderer(item)}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </CardBody>
          </Card>

          {/* Edit Items Section - Only show for non-parent tentative orders */}
          {shouldShowEditFeatures && (
            <Card className="order-card mt-3">
              <CardHeader className="order-card-header">
                <h6 className="mb-0">Edit Items ({orderData?.orderStatus})</h6>
              </CardHeader>
              <CardBody>
                <CollectItemsStep
                  watch={editWatch}
                  setOrderItems={setEditableOrderItems}
                  orderItems={editableOrderItems}
                  selectedMenu={selectedMenu as any}
                  setSelectedMenu={setSelectedMenu as any}
                  setSelectedMenuItemList={setSelectedMenuItemList as any}
                  menus={menus as any}
                  selectedMenuItemList={
                    (filteredData.length > 0
                      ? filteredData
                      : selectedMenuItemList) as any
                  }
                  setFilteredData={filteredData as any}
                  singleMenuItemsList={singleMenuItemsList as any}
                  comboItemList={comboItemList as any}
                  customMenuItemList={customMenuItemList as any}
                  setSingleMenuItemsList={setSingleMenuItemsList as any}
                  setComboItemList={setComboItemList as any}
                  setCustomMenuItemList={setCustomMenuItemList as any}
                  control={editControl as any}
                  sendCustomObj={[] as any}
                  setSendCustomObj={() => { }}
                  orderByWatcher={{ value: "CartPerson" } as any}
                  isCanCollectItems={true}
                  scheduledOrderDetails={{
                    isScheduledOrder: orderData?.isScheduledOrder || false,
                    orderTimeSlot: orderData?.deliveryTimeSlot || orderData?.pickupTimeSlot || null,
                  }}
                  isInitialLoading={isInitialLoading}
                  isTentativeOrder={true}
                  cartSummariesData={cartSummariesData}
                  isInventoryLoading={isInventoryLoading}
                  isDeliveryOrder={orderData?.isDeliveryOrder}
                  cartId={
                    orderData?.isDeliveryOrder
                      ? selectedRestaurantData?.deliveryCartId ||
                      "Tia8TM6vj1ZktAu2CqWL"
                      : orderData?.cart?.cartId
                  }
                  deliveryDate={
                    orderData?.deliverAt
                      ? typeof orderData.deliverAt === 'object' && orderData.deliverAt.seconds
                        ? new Date(orderData.deliverAt.seconds * 1000)
                        : new Date(orderData.deliverAt)
                      : undefined
                  }
                  organizationId={orderData?.organization?.organizationId || orderData?.organization?.id || null}
                  onDoneClick={async () => {
                    // Only calculate payment breakdown, don't update the order
                    if (!orderData?.id) {
                      toast.error("Order ID missing");
                      return;
                    }
                    const itemsToUpdate = editableOrderItems.map((it: any) => ({
                      menuItemId: it.id,
                      displayName: it.displayName,
                      unitPrice: it.calculatedPrice || it.price || 0,
                      quantity: it.quantity || 0,
                      itemType: it.itemType,
                      portionSize: it.portionLabel,
                      customItems: it.customItems,
                      perItemManualDiscount:
                        it.perItemManualDiscount || undefined,
                    }));
                    const computedTotal = itemsToUpdate.reduce(
                      (sum: number, it: any) =>
                        sum + (it.unitPrice || 0) * (it.quantity || 0),
                      0,
                    );

                    try {
                      // Only calculate payment breakdown to show updated totals
                      await getOrderPaymentBreakdown({
                        cartId: orderData?.cart?.cartId || undefined,
                        orderItems: itemsToUpdate.map((item: any) => ({
                          displayName: item.displayName,
                          menuItemId: item.menuItemId,
                          quantity: item.quantity,
                          unitPrice: item.unitPrice,
                          itemType: item.itemType,
                          portionSize: item.portionSize,
                          customItems: item.customItems,
                          perItemManualDiscount:
                            item.perItemManualDiscount || undefined,
                        })),
                        restaurantId: orderData?.restaurantId || "",
                        totalPrice: computedTotal,
                        promoCode: orderData?.promoCode || undefined,
                        isDeliveryOrder:
                          orderData?.isDeliveryOrder || undefined,
                        manualDeliveryFee:
                          isManualDeliveryFee ? manualDeliveryFeeAmount : undefined,
                        manualDiscount: {
                          type:
                            orderData?.paymentSummary?.manualDiscountType ||
                            undefined,
                          amount:
                            orderData?.paymentSummary?.manualDiscountAmount ||
                            undefined,
                        },
                      }).unwrap();

                      toast.success(
                        "Payment breakdown calculated successfully!",
                      );
                    } catch (error) {
                      console.error("Payment breakdown failed:", error);
                      toast.error("Failed to calculate payment breakdown");
                    }
                  }}
                />
                {/* Manual Delivery Fee Section */}
                {orderData?.isDeliveryOrder && (
                  <Row className="mt-3">
                    <Col md={12}>
                      <Card className="p-3">
                        <h6 className="mb-3">Manual Delivery Fee</h6>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Check
                                type="checkbox"
                                id="isManualDeliveryFeeCheck"
                                label="Set Manual Delivery Fee"
                                checked={isManualDeliveryFee}
                                onChange={(e) => {
                                  setIsManualDeliveryFee(e.target.checked);
                                  if (!e.target.checked) {
                                    setManualDeliveryFeeAmount(0);
                                  }
                                }}
                              />
                            </Form.Group>
                          </Col>
                          {isManualDeliveryFee && (
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Delivery Fee Amount (LKR)</Form.Label>
                                <Form.Control
                                  type="number"
                                  placeholder="Enter delivery fee"
                                  value={manualDeliveryFeeAmount}
                                  onChange={(e) => setManualDeliveryFeeAmount(Number(e.target.value))}
                                  min="0"
                                  step="0.01"
                                />
                              </Form.Group>
                            </Col>
                          )}
                        </Row>
                      </Card>
                    </Col>
                  </Row>
                )}
                {/* Editable Notes Section */}
                <Row className="mt-3">
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Order Note</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Enter order note"
                        value={editableOrderNote}
                        onChange={(e) => setEditableOrderNote(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Packing Note</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Enter packing note"
                        value={editablePackingNote}
                        onChange={(e) => setEditablePackingNote(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Payment Note</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Enter payment note"
                        value={editablePaymentNote}
                        onChange={(e) => setEditablePaymentNote(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <div className="d-flex justify-content-end mt-2">
                  <Button
                    variant="danger"
                    size="sm"
                    className="py-2 px-4 mb-3"
                    disabled={isUpdatingOrder || isPaymentLoading}
                    onClick={async () => {
                      if (!orderData?.id) {
                        toast.error("Order ID missing");
                        return;
                      }
                      const itemsToUpdate = editableOrderItems.map(
                        (it: any) => ({
                          menuItemId: it.id,
                          displayName: it.displayName,
                          unitPrice: it.calculatedPrice || it.price || 0,
                          quantity: it.quantity || 0,
                          itemType: it.itemType,
                          portionSize: it.portionLabel,
                          customItems: it.customItems,
                          perItemManualDiscount:
                            it.perItemManualDiscount || undefined,
                        }),
                      );
                      const computedTotal = itemsToUpdate.reduce(
                        (sum: number, it: any) =>
                          sum + (it.unitPrice || 0) * (it.quantity || 0),
                        0,
                      );

                      // Calculate total manual discount from per-item discounts
                      const totalPerItemDiscount = itemsToUpdate.reduce(
                        (sum: number, it: any) =>
                          sum +
                          (it.perItemManualDiscount || 0) * (it.quantity || 0),
                        0,
                      );

                      try {
                        // Calculate final price via payment breakdown API
                        const breakdown = await getOrderPaymentBreakdown({
                          cartId: orderData?.cart?.cartId || undefined,
                          orderItems: itemsToUpdate.map((item: any) => ({
                            displayName: item.displayName,
                            menuItemId: item.menuItemId,
                            quantity: item.quantity,
                            unitPrice: item.unitPrice,
                            itemType: item.itemType,
                            portionSize: item.portionSize,
                            customItems: item.customItems,
                            perItemManualDiscount:
                              item.perItemManualDiscount || undefined,
                          })),
                          restaurantId: orderData?.restaurantId || "",
                          totalPrice: computedTotal,
                          promoCode: orderData?.promoCode || undefined,
                          isDeliveryOrder:
                            orderData?.isDeliveryOrder || undefined,
                          manualDeliveryFee:
                            isManualDeliveryFee ? manualDeliveryFeeAmount : undefined,
                          manualDiscount:
                            totalPerItemDiscount > 0
                              ? {
                                type: "Fixed",
                                amount: totalPerItemDiscount,
                              }
                              : undefined,
                        }).unwrap();
                        await updateOrder({
                          orderId: orderData.id,
                          orderItems: itemsToUpdate,
                          totalPrice: breakdown?.totalPrice ?? computedTotal,
                          promoCode: orderData?.promoCode || undefined,
                          isDeliveryOrder:
                            orderData?.isDeliveryOrder || undefined,
                          manualDeliveryFee:
                            isManualDeliveryFee ? manualDeliveryFeeAmount : undefined,
                          manualDiscount:
                            totalPerItemDiscount > 0
                              ? {
                                type: "Fixed",
                                amount: totalPerItemDiscount,
                              }
                              : undefined,
                          orderNote: editableOrderNote || undefined,
                          packingNote: editablePackingNote || undefined,
                          paymentNote: editablePaymentNote || undefined,
                        }).unwrap();
                        setEditedItems(itemsToUpdate as any);
                        toast.success("Order updated successfully");
                        // handleRefresh();
                      } catch (e) {
                        toast.error("Failed to update order");
                      }
                    }}
                  >
                    {isUpdatingOrder || isPaymentLoading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                      </>
                    ) : (
                      "Save Edited Items"
                    )}
                  </Button>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Modal - Only show for non-parent tentative orders */}
          {orderData && shouldShowEditFeatures && (
            <TentativeItemsEditModal
              isOpen={isEditItemsOpen}
              onClose={() => setIsEditItemsOpen(false)}
              order={orderData}
              cartSummariesData={cartSummariesData}
              isInventoryLoading={isInventoryLoading}
              isDeliveryOrder={orderData.isDeliveryOrder}
              cartId={
                orderData.isDeliveryOrder
                  ? selectedRestaurantData?.deliveryCartId ||
                  "Tia8TM6vj1ZktAu2CqWL"
                  : orderData.cart?.cartId
              }
              isScheduledOrder={orderData.isScheduledOrder || false}
              deliveryDate={orderData.deliverAt}
              organizationId={orderData?.organization?.organizationId || orderData?.organization?.id || null}
              onSave={async (updated) => {
                if (!orderData?.id) {
                  toast.error("Order ID missing");
                  return;
                }
                try {
                  const itemsToUpdate = (updated || []).map((it: any) => {
                    // For custom menu items, use unitTotalPrice if available
                    let itemPrice =
                      it.unitPrice || it.calculatedPrice || it.price || 0;
                    if (
                      it.itemType === "CustomMenu" &&
                      it.unitTotalPrice !== undefined
                    ) {
                      itemPrice = it.unitTotalPrice;
                    }

                    return {
                      menuItemId: it.menuItemId || it.id,
                      displayName: it.displayName,
                      unitPrice: itemPrice,
                      quantity: it.quantity || 0,
                      itemType: it.itemType,
                      portionSize: it.portionSize || it.portionLabel,
                      customItems: it.customItems,
                      unitTotalPrice:
                        it.itemType === "CustomMenu"
                          ? it.unitTotalPrice
                          : undefined,
                      perItemManualDiscount:
                        it.perItemManualDiscount || undefined,
                      ...(it.tinyId && { tinyId: it.tinyId }),
                      ...(it.skuId && { skuId: it.skuId }),
                    };
                  });
                  const computedTotal = itemsToUpdate.reduce(
                    (sum: number, it: any) =>
                      sum + (it.unitPrice || 0) * (it.quantity || 0),
                    0,
                  );

                  // Calculate total manual discount from per-item discounts
                  const totalPerItemDiscount = itemsToUpdate.reduce(
                    (sum: number, it: any) =>
                      sum +
                      (it.perItemManualDiscount || 0) * (it.quantity || 0),
                    0,
                  );

                  const breakdown = await getOrderPaymentBreakdown({
                    cartId: orderData?.cart?.cartId || undefined,
                    orderItems: itemsToUpdate.map((item: any) => ({
                      displayName: item.displayName,
                      menuItemId: item.menuItemId,
                      quantity: item.quantity,
                      unitPrice: item.unitPrice,
                      itemType: item.itemType,
                      portionSize: item.portionSize,
                      customItems: item.customItems,
                      unitTotalPrice:
                        item.itemType === "CustomMenu"
                          ? item.unitTotalPrice
                          : undefined,
                      perItemManualDiscount:
                        item.perItemManualDiscount || undefined,
                    })),
                    restaurantId: orderData?.restaurantId || "",
                    totalPrice: computedTotal,
                    promoCode: orderData?.promoCode || undefined,
                    isDeliveryOrder: orderData?.isDeliveryOrder || undefined,
                    manualDeliveryFee:
                      isManualDeliveryFee ? manualDeliveryFeeAmount : undefined,
                    manualDiscount:
                      totalPerItemDiscount > 0
                        ? {
                          type: "Fixed",
                          amount: totalPerItemDiscount,
                        }
                        : undefined,
                  }).unwrap();
                  await updateOrder({
                    orderId: orderData.id,
                    orderItems: itemsToUpdate,
                    totalPrice: breakdown?.totalPrice ?? computedTotal,
                    isDeliveryOrder: orderData?.isDeliveryOrder || undefined,
                    promoCode: orderData?.promoCode || undefined,
                    manualDeliveryFee:
                      isManualDeliveryFee ? manualDeliveryFeeAmount : undefined,
                    manualDiscount:
                      totalPerItemDiscount > 0
                        ? {
                          type: "Fixed",
                          amount: totalPerItemDiscount,
                        }
                        : undefined,
                    orderNote: editableOrderNote || undefined,
                    packingNote: editablePackingNote || undefined,
                    paymentNote: editablePaymentNote || undefined,
                  }).unwrap();
                  setEditedItems(itemsToUpdate as any);
                  toast.success("Order updated successfully");
                  setIsEditItemsOpen(false);
                  // Firebase will automatically update orderData via real-time listener
                } catch (e) {
                  toast.error("Failed to update order");
                }
              }}
            />
          )}
          <StatusUpdateModal
            isOpen={isModalOpen}
            toggle={closeModal}
            order={orderData}
            isChildOrder={isChildOrder}
            isParentOrder={isParentOrder}
          />
          <DriverLocationModal
            show={showDriverLocationModal}
            onHide={handleCloseDriverLocationModal}
            cartId={
              selectedChildOrderForTracking
                ? (selectedChildOrderForTracking.deliveryCart?.cartId || selectedChildOrderForTracking.cart?.cartId)
                : orderData?.deliveryCart?.cartId || orderData?.cart?.cartId || null
            }
            orderDetails={selectedChildOrderForTracking || orderData}
          />
        </div>
      )}
    </>
  );
};

export default OrderView;
